export interface DragDropSlot {
  id: string;
  label: string;
  x: number;
  y: number;
}

export interface DragDropData {
  slots: DragDropSlot[];
  pieces: string[];
  correctMapping: Record<string, string>; // slotId -> pieceLabel
  sport?: 'football' | 'soccer' | 'basketball' | 'baseball';
}

export interface Question {
  id: string;
  type: 'multiple-choice' | 'identify' | 'fix-mistake' | 'drag-drop' | 'diagram-identify' | 'true-false' | 'scenario' | 'situational' | 'matching';
  question: string;
  diagram?: DiagramData;
  options: string[];
  correctIndex: number;
  explanation: string;
  hint?: string;
  dragDropData?: DragDropData;
  /** For true-false questions: true means the statement is true */
  correctAnswer?: boolean;
  /** For situational: game context shown above the question */
  situationContext?: string;
  /** For matching: pairs to match [left, right][] — order is shuffled */
  matchingPairs?: [string, string][];
}

export interface DiagramData {
  title: string;
  sport?: 'football' | 'soccer' | 'basketball' | 'baseball';
  players: DiagramPlayer[];
  routes?: DiagramRoute[];
}

export interface DiagramPlayer {
  x: number;
  y: number;
  type: 'offense' | 'defense';
  label?: string;
}

export interface DiagramRoute {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  type: 'solid' | 'dashed';
}

export interface Lesson {
  id: string;
  title: string;
  description: string;
  conceptText: string[];
  questions: Question[];
  xpReward: number;
}

export interface Unit {
  id: string;
  title: string;
  icon: string;
  lessons: Lesson[];
  requiredXp: number;
}

export type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';

export interface LearningPath {
  id: string;
  title: string;
  description: string;
  color: string;
  difficulty: DifficultyLevel;
  units: Unit[];
}

export const learningPaths: LearningPath[] = [
  {
    id: 'basics',
    title: 'The Basics',
    description: 'Learn formations, personnel, and the fundamentals of NFL football.',
    color: 'primary',
    difficulty: 'beginner',
    units: [
      {
        id: 'formations-101',
        title: 'Formations 101',
        icon: '🏈',
        requiredXp: 0,
        lessons: [
          {
            id: 'what-is-formation',
            title: 'What Is a Formation?',
            description: 'Learn how 11 players line up before the snap.',
            conceptText: [
              'A formation is how the offense lines up before the ball is snapped. There are always 11 players on the field.',
              'The offensive line has 5 players: Left Tackle, Left Guard, Center, Right Guard, and Right Tackle. They block for the QB.',
              'The remaining players — QB, RB, WR, TE — can be arranged in many different ways. Each arrangement is a different formation!'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'q1-tf1',
                type: 'true-false',
                question: 'Each team in football has 11 players on the field at a time.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'Correct! Each team fields exactly 11 players — on both offense and defense.'
              },
              {
                id: 'q1',
                type: 'multiple-choice',
                question: 'How many players does each team have on the field at one time?',
                options: ['10', '11', '12', '13'],
                correctIndex: 1,
                explanation: 'Each team fields exactly 11 players. The offense has 5 OL, 1 QB, and 5 skill players.'
              },
              {
                id: 'q1-tf2',
                type: 'true-false',
                question: 'The offensive line consists of 4 players.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! The offensive line always has 5 players: Left Tackle, Left Guard, Center, Right Guard, and Right Tackle.'
              },
              {
                id: 'q2',
                type: 'multiple-choice',
                question: 'How many offensive linemen are on the field?',
                options: ['3', '4', '5', '6'],
                correctIndex: 2,
                explanation: 'There are always 5 offensive linemen: LT, LG, C, RG, RT.'
              },
              {
                id: 'q2-scenario',
                type: 'scenario',
                question: 'The offense lines up with the QB directly behind the Center, a Fullback 3 yards back, and a Running Back 5 yards back — all in a straight line. The coach calls this formation by its name. What is it?',
                options: ['Shotgun Spread', 'I-Formation', 'Pistol'],
                correctIndex: 1,
                explanation: 'The I-Formation places the QB, FB, and RB in a vertical line behind the center — like the letter "I." It\'s one of football\'s most classic formations.'
              },
              {
                id: 'q3',
                type: 'identify',
                question: 'This formation has the QB under center with 2 WRs, 1 TE, and 1 RB behind the QB. What is it?',
                diagram: {
                  title: 'I-Formation',
                  players: [
                    { x: 15, y: 60, type: 'offense', label: 'WR' },
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 80, y: 60, type: 'offense', label: 'TE' },
                    { x: 90, y: 60, type: 'offense', label: 'WR' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 50, y: 74, type: 'offense', label: 'FB' },
                    { x: 50, y: 81, type: 'offense', label: 'RB' },
                  ],
                  routes: []
                },
                options: ['Shotgun Spread', 'I-Formation', 'Pistol', 'Singleback'],
                correctIndex: 1,
                explanation: 'The I-Formation places the FB and RB directly behind the QB in a vertical line — like the letter "I".'
              },
              {
                id: 'q3b',
                type: 'drag-drop',
                question: 'Build a Shotgun Spread formation. The Spread uses 5 OL, 1 QB in shotgun (5–7 yards back), 1 RB beside the QB, and 4 WRs split wide — two on each side.',
                options: [],
                correctIndex: 0,
                explanation: 'In Shotgun Spread, the five offensive linemen (LT, LG, C, RG, RT) are shoulder-to-shoulder on the line. The QB is in shotgun 5–7 yards behind center with the RB to his side. Four wide receivers split out wide, two on each side of the formation.',
                hint: 'The offensive line goes across the middle of the LOS. The QB stands well behind the center — not under center.',
                dragDropData: {
                  sport: 'football',
                  slots: [
                    // WRs split far left (on the LOS)
                    { id: 'wr1', label: '?', x: 12, y: 50 },
                    { id: 'wr2', label: '?', x: 30, y: 50 },
                    // O-Line across the middle of the LOS
                    { id: 'lt', label: '?', x: 52, y: 50 },
                    { id: 'lg', label: '?', x: 65, y: 50 },
                    { id: 'center', label: '?', x: 80, y: 50 },
                    { id: 'rg', label: '?', x: 95, y: 50 },
                    { id: 'rt', label: '?', x: 108, y: 50 },
                    // WRs split far right (on the LOS)
                    { id: 'wr3', label: '?', x: 130, y: 50 },
                    { id: 'wr4', label: '?', x: 148, y: 50 },
                    // QB in shotgun behind center
                    { id: 'qb', label: '?', x: 80, y: 70 },
                    // RB next to QB
                    { id: 'rb', label: '?', x: 95, y: 72 },
                  ],
                  pieces: ['LT', 'LG', 'C', 'RG', 'RT', 'QB', 'RB', 'WR', 'WR', 'WR', 'WR'],
                  correctMapping: {
                    lt: 'LT',
                    lg: 'LG',
                    center: 'C',
                    rg: 'RG',
                    rt: 'RT',
                    qb: 'QB',
                    rb: 'RB',
                    wr1: 'WR',
                    wr2: 'WR',
                    wr3: 'WR',
                    wr4: 'WR',
                  }
                }
              },
              {
                id: 'q3c',
                type: 'diagram-identify',
                question: 'Study the formation below. What alignment is the QB in?',
                diagram: {
                  title: '',
                  players: [
                    { x: 15, y: 60, type: 'offense', label: 'WR' },
                    { x: 25, y: 60, type: 'offense', label: 'WR' },
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 80, y: 60, type: 'offense', label: 'TE' },
                    { x: 85, y: 60, type: 'offense', label: 'WR' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 50, y: 74, type: 'offense', label: 'RB' },
                  ],
                  routes: []
                },
                options: ['Under Center', 'Shotgun', 'Pistol', 'Wildcat'],
                correctIndex: 2,
                explanation: 'The Pistol places the QB about 4 yards behind center — closer than shotgun — with the RB directly behind.'
              }
            ]
          },
          {
            id: 'shotgun-vs-undercenter',
            title: 'Shotgun vs Under Center',
            description: 'Know the difference between the two most common QB alignments.',
            conceptText: [
              'Under Center: The QB stands directly behind the Center and receives the snap hand-to-hand. Great for running plays and play-action.',
              'Shotgun: The QB stands 5-7 yards behind the Center and receives the snap as a throw. This gives the QB more time to read the defense.',
              'The Pistol is a hybrid — the QB is closer than shotgun (about 4 yards back) but still receives a thrown snap, with the RB directly behind.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'q4',
                type: 'multiple-choice',
                question: 'In shotgun formation, where does the QB stand?',
                options: ['Directly behind the center', '5-7 yards behind the center', 'On the line of scrimmage', 'In the backfield corner'],
                correctIndex: 1,
                explanation: 'In shotgun, the QB stands 5-7 yards behind center to get a better view of the defense and more time to throw.'
              },
              {
                id: 'q5',
                type: 'multiple-choice',
                question: 'Which QB alignment is best for disguising run vs pass?',
                options: ['Shotgun', 'Under Center', 'Wildcat', 'Empty'],
                correctIndex: 1,
                explanation: 'Under center is best for play-action because the QB can fake the handoff convincingly before throwing.'
              },
              {
                id: 'q6',
                type: 'fix-mistake',
                question: '"The Pistol formation places the QB 7+ yards behind center, just like the Shotgun." What\'s wrong?',
                options: ['Nothing, this is correct', 'The Pistol is only 4 yards back, closer than Shotgun', 'The Pistol is under center', 'The Pistol doesn\'t use a center'],
                correctIndex: 1,
                explanation: 'The Pistol is a hybrid — the QB is only about 4 yards back (closer than shotgun\'s 5-7 yards) and the RB lines up directly behind.'
              }
            ]
          },
          {
            id: 'personnel-groups',
            title: 'Personnel Groupings',
            description: 'Decode numbers like "11 personnel" and "22 personnel."',
            conceptText: [
              'Personnel groupings use two numbers: the first is the number of RBs, the second is the number of TEs on the field.',
              '11 Personnel = 1 RB, 1 TE, 3 WR. This is the most common grouping in the modern NFL — used on about 60% of plays!',
              '12 Personnel = 1 RB, 2 TE, 2 WR. Great for running AND passing. 21 Personnel = 2 RB, 1 TE, 2 WR. More run-heavy.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'q7',
                type: 'multiple-choice',
                question: 'In "11 Personnel," how many wide receivers are on the field?',
                options: ['1', '2', '3', '4'],
                correctIndex: 2,
                explanation: '11 = 1 RB + 1 TE. That\'s 2 of the 5 skill positions used, leaving 3 WR spots. 5 - 1 - 1 = 3 WR.'
              },
              {
                id: 'q8',
                type: 'multiple-choice',
                question: 'Which personnel grouping is the most common in today\'s NFL?',
                options: ['22 Personnel', '21 Personnel', '11 Personnel', '10 Personnel'],
                correctIndex: 2,
                explanation: '11 Personnel (1 RB, 1 TE, 3 WR) is used on about 60% of NFL plays. It\'s versatile for both run and pass.'
              },
              {
                id: 'q9',
                type: 'identify',
                question: 'A team has 2 running backs, 2 tight ends, and 1 wide receiver on the field. What personnel is this?',
                options: ['21 Personnel', '12 Personnel', '22 Personnel', '11 Personnel'],
                correctIndex: 2,
                explanation: '2 RBs + 2 TEs = "22 Personnel." This is a heavy, run-oriented grouping.'
              }
            ]
          }
        ]
      },
      {
        id: 'run-vs-pass',
        title: 'Run vs Pass',
        icon: '🏃',
        requiredXp: 50,
        lessons: [
          {
            id: 'running-game-basics',
            title: 'The Running Game',
            description: 'Inside zone, outside zone, power, and counter.',
            conceptText: [
              'Running plays aim to advance the ball on the ground. The offensive line creates lanes (holes) for the running back to run through.',
              'Inside Zone: The entire OL blocks in one direction. The RB reads the blocks and finds the best hole. It\'s the most common NFL run scheme.',
              'Outside Zone: Similar concept but the RB aims for the edge of the formation. The goal is to "stretch" the defense horizontally and find a cutback lane.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'q10',
                type: 'multiple-choice',
                question: 'In an Inside Zone run, what does the running back do after receiving the handoff?',
                options: ['Runs to a predetermined hole called by the QB', 'Reads the defensive movement and finds the best lane', 'Always follows the fullback through the A-gap', 'Waits for the pulling guard to lead the way'],
                correctIndex: 1,
                explanation: 'In zone blocking, the RB reads how the defense reacts to the blocks and chooses the best running lane — it\'s not a predetermined hole.'
              },
              {
                id: 'q11',
                type: 'multiple-choice',
                question: 'What is the goal of an Outside Zone run?',
                options: ['Hit the A-gap as fast as possible', 'Stretch the defense horizontally and find a cutback lane', 'Set up a play-action pass on the next play', 'Get the running back to the sideline for extra yards'],
                correctIndex: 1,
                explanation: 'Outside Zone stretches the defense sideline-to-sideline, hoping the RB can get the edge or find a cutback lane when defenders overcommit.'
              },
              {
                id: 'q12',
                type: 'fix-mistake',
                question: '"In zone blocking, each lineman blocks a specific defender assigned to them." What\'s wrong here?',
                options: ['Nothing, this is correct', 'Zone blocking means linemen block an area, not a specific man', 'Zone blocking only works with 2 TEs', 'Zone blocking is only for passing plays'],
                correctIndex: 1,
                explanation: 'Zone blocking means each lineman blocks an area/zone, NOT a specific defender. Man blocking assigns specific defenders to each lineman.'
              }
            ]
          },
          {
            id: 'passing-game-basics',
            title: 'The Passing Game',
            description: 'Route trees, timing, and the passing attack.',
            conceptText: [
              'The passing game relies on receivers running specific patterns called "routes." The QB reads the defense and throws to the open receiver.',
              'The Route Tree has 9 basic routes numbered 0-9: Hitch (0), Flat (1), Slant (2), Comeback (3), Curl (4), Out (5), Dig/In (6), Corner (7), Post (8), Go/Fly (9).',
              'Odd numbers break outside, even numbers break inside. A "Go" route (9) is just a sprint straight upfield. This numbering system lets coaches call plays quickly!'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'q13',
                type: 'multiple-choice',
                question: 'How many basic routes are on the standard Route Tree?',
                options: ['5', '7', '9', '11'],
                correctIndex: 2,
                explanation: 'The standard route tree has 9 basic routes (sometimes 10 if you count the hitch as route 0), numbered for quick playcalling.'
              },
              {
                id: 'q14',
                type: 'multiple-choice',
                question: 'A receiver runs straight upfield as fast as possible. What route is this?',
                options: ['Slant', 'Curl', 'Post', 'Go/Fly'],
                correctIndex: 3,
                explanation: 'A Go (or Fly) route is route #9 — the receiver sprints straight downfield to stretch the defense vertically.'
              },
              {
                id: 'q15',
                type: 'identify',
                question: 'A receiver runs 5 yards upfield, then cuts sharply toward the middle of the field. What route is this?',
                options: ['Out route', 'Slant', 'Dig/In route', 'Corner route'],
                correctIndex: 2,
                explanation: 'A Dig (or In) route is route #6 — the receiver goes upfield then breaks inside across the middle.'
              },
              {
                id: 'q15b',
                type: 'diagram-identify',
                question: 'Look at the route the outside WR is running. What route concept is this?',
                diagram: {
                  title: '',
                  players: [
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 10, y: 60, type: 'offense', label: 'WR' },
                    { x: 90, y: 60, type: 'offense', label: 'WR' },
                  ],
                  routes: [
                    { fromX: 10, fromY: 60, toX: 10, toY: 40, type: 'solid' },
                    { fromX: 10, fromY: 40, toX: 30, toY: 25, type: 'solid' },
                  ]
                },
                options: ['Slant', 'Post', 'Corner', 'Out'],
                correctIndex: 1,
                explanation: 'A Post route goes upfield then breaks diagonally toward the goalposts (middle of the field). The angled break inside is the key identifier.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'offense',
    difficulty: 'intermediate',
    title: 'Offense Concepts',
    description: 'Master the plays and schemes that move the chains.',
    color: 'primary',
    units: [
      {
        id: 'play-action',
        title: 'Play-Action & RPOs',
        icon: '🎭',
        requiredXp: 100,
        lessons: [
          {
            id: 'play-action-101',
            title: 'Play-Action Passing',
            description: 'Fake the run, throw the pass.',
            conceptText: [
              'Play-action is when the QB fakes a handoff to the RB before throwing a pass. It\'s designed to freeze linebackers and safeties who read "run."',
              'Play-action works best when the defense respects the run. If a team has been running successfully, the defense bites hard on fakes.',
              'Kyle Shanahan and Sean McVay are masters of play-action — their offenses build everything off the outside zone run fake.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q16',
                type: 'multiple-choice',
                question: 'What is the primary purpose of a play-action pass?',
                options: ['To give the QB extra time by rolling out of the pocket', 'To freeze defenders who are expecting a run', 'To set up a screen pass behind the line', 'To allow the RB to release as a receiver'],
                correctIndex: 1,
                explanation: 'Play-action freezes linebackers and safeties who read run keys, creating open windows for receivers downfield.'
              },
              {
                id: 'q17',
                type: 'multiple-choice',
                question: 'When does play-action work BEST?',
                options: ['On 3rd and long when the defense expects pass', 'When the defense already respects the team\'s run game', 'In the two-minute drill at the end of a half', 'When using empty backfield sets with no RB'],
                correctIndex: 1,
                explanation: 'Play-action is most effective when the defense fears the run — they bite harder on the fake, leaving receivers open downfield.'
              },
              {
                id: 'q18',
                type: 'fix-mistake',
                question: '"Play-action only works from Shotgun because the QB needs distance from the line." Is this correct?',
                options: ['Yes, this is correct', 'No — play-action is most effective from Under Center because the fake looks more realistic', 'No — play-action doesn\'t involve faking', 'Yes, but only in the red zone'],
                correctIndex: 1,
                explanation: 'Play-action is most convincing from under center because the QB can execute a realistic-looking handoff fake.'
              }
            ]
          },
          {
            id: 'rpo-basics',
            title: 'RPO: Run-Pass Option',
            description: 'Let the QB decide AFTER the snap.',
            conceptText: [
              'An RPO (Run-Pass Option) gives the QB a choice: hand the ball off for a run OR throw a quick pass — based on what ONE defender does after the snap.',
              'The QB "reads" a specific defender (usually a linebacker). If the LB drops into coverage → hand off. If the LB crashes toward the run → throw it.',
              'RPOs are different from play-action! In an RPO, the run IS real — the OL blocks for the run. In play-action, the run is always a fake.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q19',
                type: 'multiple-choice',
                question: 'What makes an RPO different from play-action?',
                options: ['The OL blocks differently on RPOs', 'In an RPO the run is a real option — the QB decides post-snap whether to hand off or throw', 'RPOs are only used on first down', 'Play-action always includes a read of the defense'],
                correctIndex: 1,
                explanation: 'In an RPO, the run IS real — the QB decides post-snap whether to hand off or throw. In play-action, the handoff is always a fake.'
              },
              {
                id: 'q20',
                type: 'multiple-choice',
                question: 'In a typical RPO, who does the QB "read" after the snap?',
                options: ['The safety', 'A cornerback', 'A linebacker', 'The nose tackle'],
                correctIndex: 2,
                explanation: 'The QB typically reads a linebacker. If the LB crashes to stop the run, the QB pulls the ball and throws to the space the LB vacated.'
              }
            ]
          }
        ]
      },
      {
        id: 'route-concepts',
        title: 'Route Concepts',
        icon: '📐',
        requiredXp: 150,
        lessons: [
          {
            id: 'mesh-concept',
            title: 'The Mesh Concept',
            description: 'Two receivers crossing creates chaos for man coverage.',
            conceptText: [
              'Mesh is a pass concept where two receivers run shallow crossing routes in opposite directions, passing very close to each other (within 1 yard).',
              'Against man coverage, the crossing creates "picks" — the trailing defenders collide with each other trying to follow their assigned receivers.',
              'Mesh is one of the best man-beater concepts in football. If you see a defense playing tight man coverage, Mesh is a great call.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q21',
                type: 'multiple-choice',
                question: 'What type of coverage does the Mesh concept beat best?',
                options: ['Cover 2 Zone', 'Cover 3 Zone', 'Man-to-man coverage', 'Cover 4 (Quarters)'],
                correctIndex: 2,
                explanation: 'Mesh is a premier man-beater. The crossing routes create natural picks that trip up man-coverage defenders trying to follow their assignments.'
              },
              {
                id: 'q22',
                type: 'identify',
                question: 'Two receivers run shallow crossers in opposite directions, nearly touching as they pass. What concept is this?',
                diagram: {
                  title: 'Mesh Concept',
                  players: [
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 15, y: 60, type: 'offense', label: 'WR' },
                    { x: 90, y: 60, type: 'offense', label: 'WR' },
                  ],
                  routes: [
                    { fromX: 15, fromY: 60, toX: 75, toY: 48, type: 'solid' },
                    { fromX: 90, fromY: 60, toX: 25, toY: 48, type: 'solid' },
                  ]
                },
                options: ['Flood', 'Mesh', 'Y-Cross', 'Smash'],
                correctIndex: 1,
                explanation: 'Mesh! The two shallow crossers passing close together is the signature of this man-coverage-beating concept.'
              }
            ]
          },
          {
            id: 'flood-concept',
            title: 'The Flood Concept',
            description: 'Three receivers on one side, three levels.',
            conceptText: [
              'Flood sends three receivers to one side of the field at three different depths: short (flat), medium (out/curl), and deep (corner/go).',
              'The idea is to outnumber the defense on one side. If there are only 2 defenders covering that zone, someone MUST be open.',
              'Flood is a classic zone-beater. It stresses Cover 2 and Cover 3 by putting defenders in conflict — they can\'t cover two receivers at once.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q23',
                type: 'multiple-choice',
                question: 'How many levels of receivers does the Flood concept use?',
                options: ['1', '2', '3', '4'],
                correctIndex: 2,
                explanation: 'Flood uses three levels — short, medium, and deep — to create a 3-on-2 advantage against zone coverage.'
              },
              {
                id: 'q24',
                type: 'multiple-choice',
                question: 'Flood is primarily designed to beat what type of coverage?',
                options: ['Man Coverage', 'Zone Coverage', 'Blitzes', 'Goal Line Defense'],
                correctIndex: 1,
                explanation: 'Flood beats zone by outnumbering defenders in one area. Zone defenders can\'t cover three levels with only two players.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'defense',
    difficulty: 'intermediate',
    title: 'Defense Concepts',
    description: 'Learn coverage shells, blitz packages, and how to stop the pass.',
    color: 'primary',
    units: [
      {
        id: 'coverage-shells',
        title: 'Coverage Shells',
        icon: '🛡️',
        requiredXp: 100,
        lessons: [
          {
            id: 'cover-1',
            title: 'Cover 1 (Man Free)',
            description: 'Man coverage with a single high safety.',
            conceptText: [
              'Cover 1 means one deep safety playing center field, with everyone else in man-to-man coverage on their assigned receiver.',
              'The free safety is the "last line of defense" — he helps over the top on deep routes. This lets the corners play aggressive press man.',
              'Cover 1 is vulnerable to: pick plays (like Mesh), crossing routes, and rub concepts that cause defenders to collide.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q25',
                type: 'multiple-choice',
                question: 'In Cover 1, how many safeties play deep?',
                options: ['0', '1', '2', '3'],
                correctIndex: 1,
                explanation: 'Cover 1 = 1 deep safety. That\'s where the name comes from! The single high safety provides help over the top.'
              },
              {
                id: 'q26',
                type: 'multiple-choice',
                question: 'Cover 1 is vulnerable to which type of concept?',
                options: ['Inside runs', 'Screen passes', 'Pick/rub routes like Mesh', 'Quarterback sneaks'],
                correctIndex: 2,
                explanation: 'Pick and rub routes (like Mesh) create traffic that man-coverage defenders can\'t navigate, creating easy separation.'
              }
            ]
          },
          {
            id: 'cover-2',
            title: 'Cover 2',
            description: 'Two deep safeties splitting the field in half.',
            conceptText: [
              'Cover 2 has two safeties, each covering half the deep field. The corners play the flats (short outside zones).',
              'Cover 2\'s weakness is the deep middle of the field — the "hole" between the two safeties. A well-thrown post or seam route can split them.',
              'Tampa 2 is a famous variation where the Mike linebacker drops deep into the middle to cover that hole. Tony Dungy made it famous with the Buccaneers.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q27',
                type: 'multiple-choice',
                question: 'What is Cover 2\'s biggest weakness?',
                options: ['Short passes to the flat', 'Deep middle of the field', 'Outside runs', 'Quarterback scrambles'],
                correctIndex: 1,
                explanation: 'The gap between the two safeties in the deep middle is Cover 2\'s Achilles\' heel. Post routes exploit this.'
              },
              {
                id: 'q28',
                type: 'multiple-choice',
                question: 'In Tampa 2, which player drops deep to help cover the middle?',
                options: ['The nickel corner', 'The strong safety', 'The Mike (middle) linebacker', 'The defensive end'],
                correctIndex: 2,
                explanation: 'Tampa 2\'s innovation: the Mike linebacker drops deep into the middle hole, turning Cover 2\'s weakness into a strength.'
              },
              {
                id: 'q29',
                type: 'fix-mistake',
                question: '"In Cover 2, the corners drop deep and the safeties play the flats." What\'s wrong?',
                options: ['Nothing, this is correct', 'It\'s reversed — safeties play deep, corners play the flats', 'Both safeties AND corners play deep', 'Only one corner plays the flat'],
                correctIndex: 1,
                explanation: 'In Cover 2, the safeties split the deep field while the corners drop into short flat zones. Not the other way around!'
              }
            ]
          },
          {
            id: 'cover-3',
            title: 'Cover 3',
            description: 'Three deep defenders, each covering a third of the field.',
            conceptText: [
              'Cover 3 divides the deep field into thirds. Typically: 1 safety + 2 corners each drop deep to cover their third.',
              'The remaining 4 underneath defenders (LBs and a safety/nickel) cover short zones. This gives you 7 in coverage total.',
              'Cover 3 is great against deep passes but can be attacked with the "4 Verticals" concept — sending 4 receivers deep to overload 3 deep defenders.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q30',
                type: 'multiple-choice',
                question: 'How is the deep field divided in Cover 3?',
                options: ['In half', 'In thirds', 'In quarters', 'One player covers all deep'],
                correctIndex: 1,
                explanation: 'Cover 3 = 3 deep zones. The field is split into thirds, each covered by one defender.'
              },
              {
                id: 'q31',
                type: 'multiple-choice',
                question: 'Which concept is designed to beat Cover 3?',
                options: ['Screen pass', 'QB sneak', '4 Verticals', 'Draw play'],
                correctIndex: 2,
                explanation: '4 Verticals sends four receivers deep — that\'s 4 vs 3, guaranteeing someone is in a soft spot between zones.'
              }
            ]
          },
          {
            id: 'cover-4',
            title: 'Cover 4 (Quarters)',
            description: 'Four deep defenders, each covering a quarter of the field.',
            conceptText: [
              'Cover 4 (Quarters) divides the deep field into four zones — each covered by a safety or corner. It\'s the most conservative deep coverage in football.',
              'Quarters is excellent against deep passing attacks and 4 Verticals because you have a defender for each vertical route. It also allows safeties to come up and support the run.',
              'The weakness? Underneath coverage is thin. With 4 defenders deep, only 3 defenders remain underneath. Quick slants, drags, and crossing routes can carve up the short zones.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q32',
                type: 'multiple-choice',
                question: 'How many defenders play deep in Cover 4 (Quarters)?',
                options: ['2', '3', '4', '5'],
                correctIndex: 2,
                explanation: 'Cover 4 = 4 deep defenders — two corners and two safeties, each covering a quarter of the deep field.'
              },
              {
                id: 'q33',
                type: 'multiple-choice',
                question: 'What is Cover 4\'s biggest weakness?',
                options: ['Deep passes', 'QB scrambles', 'Short/underneath routes', 'Outside runs'],
                correctIndex: 2,
                explanation: 'With 4 deep, only 3 remain underneath. Short crossing routes, slants, and drags exploit the thin underneath coverage.'
              },
              {
                id: 'q34',
                type: 'fix-mistake',
                question: '"Cover 4 is terrible against 4 Verticals because it only has 3 deep defenders." What\'s wrong?',
                options: ['Nothing, this is correct', 'Cover 4 has 4 deep defenders — one for each vertical route, making it ideal against 4 Verts', 'Cover 4 has 5 deep defenders', 'Cover 4 is a man coverage scheme'],
                correctIndex: 1,
                explanation: 'Cover 4 is specifically designed to handle 4 Verticals — each of the 4 deep defenders can match up with one vertical route.'
              },
              {
                id: 'q35',
                type: 'multiple-choice',
                question: 'Which concept would you call to beat Cover 4 underneath?',
                options: ['4 Verticals', 'Hail Mary', 'Mesh (shallow crossers)', 'Prevent Defense'],
                correctIndex: 2,
                explanation: 'Mesh attacks the thin underneath zones with crossing routes that make the 3 remaining defenders cover too much ground.'
              }
            ]
          }
        ]
      },
      {
        id: 'blitz-packages',
        title: 'Blitz Packages',
        icon: '⚡',
        requiredXp: 200,
        lessons: [
          {
            id: 'blitz-basics',
            title: 'What Is a Blitz?',
            description: 'Sending extra rushers to pressure the QB.',
            conceptText: [
              'A blitz sends more than the standard 4 rushers after the quarterback. Linebackers, safeties, or even corners can blitz.',
              'The trade-off: every extra rusher means one fewer defender in coverage. A 5-man blitz leaves 6 in coverage. A 6-man blitz leaves only 5.',
              'Blitzes create pressure but create risk. If the QB reads the blitz and finds the vacated area, it can be a big play for the offense.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q36',
                type: 'multiple-choice',
                question: 'A defense sends 5 rushers instead of the standard 4. What is the trade-off?',
                options: ['The defensive line gets slower', 'One fewer defender remains in pass coverage', 'The secondary must play tighter man coverage', 'The blitz only works from one side'],
                correctIndex: 1,
                explanation: 'Every blitzer is one fewer coverage defender. A blitz creates pressure but opens holes in coverage if the QB can get the ball out quickly.'
              },
              {
                id: 'q37',
                type: 'multiple-choice',
                question: 'If a defense sends a 6-man blitz, how many players remain in coverage?',
                options: ['4', '5', '6', '7'],
                correctIndex: 1,
                explanation: '11 players - 6 rushers = 5 in coverage. That\'s very aggressive and leaves the secondary vulnerable to big plays.'
              },
              {
                id: 'q38',
                type: 'identify',
                question: 'The defense sends both a linebacker AND a safety after the QB while the rest play zone. What is this?',
                options: ['A standard 4-man rush', 'A zone blitz', 'A prevent defense', 'A QB spy'],
                correctIndex: 1,
                explanation: 'A zone blitz brings extra rushers while the remaining defenders play zone coverage — disguising who\'s coming and who\'s dropping.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'advanced-offense',
    difficulty: 'advanced',
    title: 'Advanced Offense',
    description: 'Power runs, advanced route concepts, and situational football.',
    color: 'primary',
    units: [
      {
        id: 'power-running',
        title: 'Power & Counter',
        icon: '💪',
        requiredXp: 200,
        lessons: [
          {
            id: 'power-run',
            title: 'Power Run Scheme',
            description: 'Gap blocking with a pulling guard.',
            conceptText: [
              'Power is a gap-blocking scheme — instead of zone blocking an area, each lineman blocks a specific gap. The key is a pulling guard who leads the way.',
              'The play-side guard and tackle double-team the defensive tackle, while the backside guard "pulls" — running behind the line to the play-side to lead-block through the hole.',
              'Power is a downhill, physical run. It works best with a big, powerful running back who can follow the pulling guard and finish runs through contact.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q39',
                type: 'multiple-choice',
                question: 'What is the fundamental difference between Power and Zone run schemes?',
                options: ['Power uses more wide receivers than Zone', 'Power assigns specific gap responsibilities and features a pulling guard, while Zone has linemen blocking areas', 'Zone is only used from Shotgun, Power only from Under Center', 'Power is for short yardage only, Zone is for any distance'],
                correctIndex: 1,
                explanation: 'Power is a gap scheme — linemen block assigned gaps, and a guard pulls to lead the RB through the hole. Zone has linemen blocking areas reactively.'
              },
              {
                id: 'q40',
                type: 'multiple-choice',
                question: 'In a Power run, which lineman typically "pulls" to lead block?',
                options: ['The center', 'The backside guard', 'The tight end', 'The left tackle'],
                correctIndex: 1,
                explanation: 'The backside guard pulls across the formation to the play-side, becoming the lead blocker for the running back through the hole.'
              },
              {
                id: 'q41',
                type: 'identify',
                question: 'A run play where the backside guard pulls to lead block, the play-side OL double-teams, and the RB hits the hole downhill. What scheme is this?',
                diagram: {
                  title: 'Power Run',
                  players: [
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 50, y: 72, type: 'offense', label: 'RB' },
                    { x: 80, y: 60, type: 'offense', label: 'TE' },
                  ],
                  routes: [
                    { fromX: 40, fromY: 60, toX: 65, toY: 55, type: 'dashed' },
                    { fromX: 50, fromY: 72, toX: 65, toY: 55, type: 'solid' },
                  ]
                },
                options: ['Inside Zone', 'Power', 'Counter', 'Draw'],
                correctIndex: 1,
                explanation: 'Power! The pulling guard (LG) leads through the hole while the RB follows downhill. Classic gap scheme football.'
              }
            ]
          },
          {
            id: 'counter-run',
            title: 'Counter Run Scheme',
            description: 'Misdirection with two pulling blockers.',
            conceptText: [
              'Counter is Power\'s sneaky cousin. The RB takes a false step one direction (the "counter" step), then cuts back the other way behind TWO pulling blockers.',
              'Typically, the backside guard AND backside tackle (or TE) both pull to the play-side. The first puller kicks out the edge defender, the second leads through the hole.',
              'Counter is devastating against aggressive defenses that flow hard to the ball. The misdirection freezes linebackers just long enough to create running lanes.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q42',
                type: 'multiple-choice',
                question: 'How many blockers typically pull on a Counter run?',
                options: ['0', '1', '2', '3'],
                correctIndex: 2,
                explanation: 'Counter features TWO pulling blockers — usually the backside guard and tackle. The first kicks out, the second leads through.'
              },
              {
                id: 'q43',
                type: 'multiple-choice',
                question: 'What makes Counter different from Power?',
                options: ['Counter has no pulling blockers', 'Counter adds a misdirection step and uses two pullers instead of one', 'Counter is only for passing', 'Counter is the same as Inside Zone'],
                correctIndex: 1,
                explanation: 'Counter adds misdirection (the RB\'s false step) and uses two pullers instead of Power\'s one, creating a more deceptive look.'
              },
              {
                id: 'q44',
                type: 'fix-mistake',
                question: '"Counter works best against patient defenses that don\'t move much." What\'s wrong?',
                options: ['Nothing, this is correct', 'Counter actually works best against aggressive defenses that flow hard — the misdirection freezes them', 'Counter doesn\'t use misdirection', 'Counter is only for short yardage'],
                correctIndex: 1,
                explanation: 'Counter exploits aggression! When defenders flow hard to stop the run, the misdirection step freezes them and the pullers create lanes going the other way.'
              }
            ]
          }
        ]
      },
      {
        id: 'four-verticals',
        title: '4 Verticals & Deep Shots',
        icon: '🚀',
        requiredXp: 250,
        lessons: [
          {
            id: 'four-verts',
            title: '4 Verticals',
            description: 'Send four receivers deep to stress the defense.',
            conceptText: [
              '4 Verticals sends four receivers straight downfield, one in each quarter of the field. It\'s the ultimate deep-field stress test for any defense.',
              'Against Cover 3 (3 deep defenders), 4 Verticals creates a 4-on-3 advantage. One receiver MUST be open between the zones.',
              'Smart QBs read the safeties: if the safety cheats to one side, throw to the opposite seam. The key reads are the two inside seam routes between the hash marks.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q45',
                type: 'multiple-choice',
                question: '4 Verticals is specifically designed to beat which coverage?',
                options: ['Cover 0 (no deep safety)', 'Cover 1 (1 deep)', 'Cover 3 (3 deep)', 'Cover 4 (4 deep)'],
                correctIndex: 2,
                explanation: '4 Verts puts 4 receivers deep against 3 deep defenders in Cover 3 — creating a guaranteed numerical advantage.'
              },
              {
                id: 'q46',
                type: 'multiple-choice',
                question: 'In 4 Verticals, which routes are the primary reads for the QB?',
                options: ['The two outside go routes', 'The two inside seam routes', 'The running back check-down', 'The tight end drag'],
                correctIndex: 1,
                explanation: 'The two inside seam routes are the money reads. The QB watches the safeties and throws to whichever seam the safety vacates.'
              },
              {
                id: 'q47',
                type: 'identify',
                question: 'Four receivers all sprint straight downfield, stretching the defense vertically across the entire width of the field. What concept?',
                diagram: {
                  title: '4 Verticals',
                  players: [
                    { x: 30, y: 60, type: 'offense', label: 'LT' },
                    { x: 40, y: 60, type: 'offense', label: 'LG' },
                    { x: 50, y: 60, type: 'offense', label: 'C' },
                    { x: 60, y: 60, type: 'offense', label: 'RG' },
                    { x: 70, y: 60, type: 'offense', label: 'RT' },
                    { x: 50, y: 67, type: 'offense', label: 'QB' },
                    { x: 10, y: 60, type: 'offense', label: 'X' },
                    { x: 35, y: 58, type: 'offense', label: 'H' },
                    { x: 65, y: 58, type: 'offense', label: 'Y' },
                    { x: 90, y: 60, type: 'offense', label: 'Z' },
                  ],
                  routes: [
                    { fromX: 10, fromY: 60, toX: 10, toY: 15, type: 'solid' },
                    { fromX: 35, fromY: 58, toX: 35, toY: 15, type: 'solid' },
                    { fromX: 65, fromY: 58, toX: 65, toY: 15, type: 'solid' },
                    { fromX: 90, fromY: 60, toX: 90, toY: 15, type: 'solid' },
                  ]
                },
                options: ['Flood', 'Mesh', '4 Verticals', 'Smash'],
                correctIndex: 2,
                explanation: '4 Verticals! All four receivers run go routes, stretching every deep zone to its limit.'
              }
            ]
          },
          {
            id: 'y-cross',
            title: 'The Y-Cross Concept',
            description: 'A deep crosser that attacks the middle of the field.',
            conceptText: [
              'Y-Cross sends the Y receiver (usually a TE or slot) on a deep crossing route from one side of the field to the other, typically 15-20 yards downfield.',
              'The deep crosser is paired with a post route on the other side. Together, they stress the deep middle — one of them will find a void in the coverage.',
              'Y-Cross is a "Cover 2 killer." The deep cross sits right in the hole between the two safeties, and the post pulls one safety away.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q48',
                type: 'multiple-choice',
                question: 'What coverage does Y-Cross specifically attack?',
                options: ['Cover 0', 'Cover 1', 'Cover 2', 'Cover 4'],
                correctIndex: 2,
                explanation: 'Y-Cross attacks the deep middle hole in Cover 2. The crossing route sits between the two safeties in the vulnerable seam.'
              },
              {
                id: 'q49',
                type: 'multiple-choice',
                question: 'What route is typically paired with the deep crosser in Y-Cross?',
                options: ['A screen pass', 'A post route', 'A flat route', 'A quarterback sneak'],
                correctIndex: 1,
                explanation: 'A post route pulls one safety toward the middle, creating space for the deep crosser on the other side.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'situational',
    difficulty: 'advanced',
    title: 'Situational Football',
    description: 'Red zone, goal line, and game-winning strategies.',
    color: 'primary',
    units: [
      {
        id: 'red-zone',
        title: 'Red Zone Offense',
        icon: '🎯',
        requiredXp: 300,
        lessons: [
          {
            id: 'red-zone-basics',
            title: 'Red Zone Fundamentals',
            description: 'Why everything changes inside the 20.',
            conceptText: [
              'The Red Zone is the area between the opponent\'s 20-yard line and the goal line. Scoring touchdowns here (instead of settling for field goals) wins championships.',
              'The field shrinks dramatically: the defense has less ground to cover, deep routes become shorter, and there\'s less room to stretch the defense.',
              'NFL teams convert Red Zone trips into TDs about 55-60% of the time. Elite offenses like the Chiefs and 49ers push that above 65%.'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q50',
                type: 'multiple-choice',
                question: 'Why is the Red Zone harder for offenses?',
                options: ['Defenders can play more aggressively near the goal line', 'The compressed field gives defenders less ground to cover', 'The play clock is shorter inside the 20', 'Teams must use heavier personnel groupings'],
                correctIndex: 1,
                explanation: 'Inside the 20, the defense has much less ground to cover. Deep routes are compressed and defenders can play tighter coverage.'
              },
              {
                id: 'q51',
                type: 'multiple-choice',
                question: 'What percentage of Red Zone trips do NFL teams typically convert to TDs?',
                options: ['30-35%', '45-50%', '55-60%', '75-80%'],
                correctIndex: 2,
                explanation: 'The NFL average is about 55-60% TD conversion in the Red Zone. Elite offenses push above 65%.'
              },
              {
                id: 'q52',
                type: 'fix-mistake',
                question: '"In the Red Zone, teams should always throw deep because defenders are playing tight." What\'s wrong?',
                options: ['Nothing, this is correct', 'Deep routes are compressed in the Red Zone — there\'s no room for deep shots', 'Teams can\'t throw in the Red Zone', 'Only running plays work in the Red Zone'],
                correctIndex: 1,
                explanation: 'There\'s no room for deep routes inside the 20! The end zone acts as a boundary. Quick passes, play-action, and power runs dominate here.'
              }
            ]
          },
          {
            id: 'red-zone-concepts',
            title: 'Red Zone Play Concepts',
            description: 'Smash, Fade-Out, and goal line schemes.',
            conceptText: [
              'The Smash concept is a Red Zone staple: one receiver runs a hitch at 5 yards while another runs a corner route behind him. It creates a high-low read for the QB.',
              'Fade routes to the back corner of the end zone are the classic Red Zone throw — the WR uses his body to shield the DB and the QB throws where only his guy can catch it.',
              'Goal Line offense (inside the 5) goes heavy: 22 or 23 personnel, multiple TEs, fullbacks. Power and Dive are the most common plays. QB sneaks convert at over 80%!'
            ],
            xpReward: 30,
            questions: [
              {
                id: 'q53',
                type: 'multiple-choice',
                question: 'What is the Smash concept in the Red Zone?',
                options: ['A run play up the middle', 'A hitch-corner combo creating a high-low read', 'A screen pass to the RB', 'A Hail Mary deep shot'],
                correctIndex: 1,
                explanation: 'Smash pairs a short hitch with a deeper corner route, forcing the defender to choose: cover short or deep? Either way, someone\'s open.'
              },
              {
                id: 'q54',
                type: 'multiple-choice',
                question: 'Inside the 5-yard line, which play has the highest conversion rate?',
                options: ['Play-action pass', 'Outside zone run', 'QB sneak', 'Screen pass'],
                correctIndex: 2,
                explanation: 'QB sneaks convert at over 80% inside the 5! The QB follows the center\'s surge for a short push forward. Tush Push, anyone?'
              },
              {
                id: 'q55',
                type: 'identify',
                question: 'One receiver runs a 5-yard hitch while another runs a corner route to the back of the end zone. What Red Zone concept is this?',
                options: ['Flood', 'Mesh', 'Smash', '4 Verticals'],
                correctIndex: 2,
                explanation: 'Smash! The hitch-corner combo is one of the most reliable Red Zone passing concepts in football.'
              }
            ]
          },
          {
            id: 'red-zone-defense',
            title: 'Red Zone Defense',
            description: 'How defenses tighten up inside the 20.',
            conceptText: [
              'Red Zone defense is about compression. With less field to cover, defenders can play tighter man coverage and more aggressive zone schemes.',
              'Many teams switch to more man coverage in the Red Zone because there\'s less deep space for receivers to exploit. Press man at the line is very effective.',
              'Goal line defense often uses "Bear" fronts with extra defensive linemen, sometimes going 6-1 (6 DL, 1 LB) to stuff the run and take away QB sneaks.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'q56',
                type: 'multiple-choice',
                question: 'Why do defenses often switch to more man coverage in the Red Zone?',
                options: ['Man coverage is easier to learn', 'Less deep space makes man coverage safer — receivers can\'t run away', 'Zone coverage is illegal in the Red Zone', 'They don\'t switch at all'],
                correctIndex: 1,
                explanation: 'With the end zone compressing the field, there\'s less room for receivers to create deep separation, making man coverage much more effective.'
              },
              {
                id: 'q57',
                type: 'multiple-choice',
                question: 'What is a "Bear" front in goal line defense?',
                options: ['A 3-4 defense', 'An extra DB package', 'A 6-1 front with extra defensive linemen to stuff the run', 'A cover 2 zone'],
                correctIndex: 2,
                explanation: 'Bear fronts load up the line of scrimmage with extra DL — sometimes 6 or even 7 — to stop power runs and QB sneaks at the goal line.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'rules',
    difficulty: 'beginner',
    title: 'Football Rules',
    description: 'Understand the rules that govern the game — from downs to penalties.',
    color: 'primary',
    units: [
      {
        id: 'game-structure',
        title: 'How the Game Works',
        icon: '📋',
        requiredXp: 0,
        lessons: [
          {
            id: 'downs-system',
            title: 'The Downs System',
            description: 'First down, fourth down, and everything in between.',
            conceptText: [
              'The offense gets 4 attempts (downs) to move the ball 10 yards. If they succeed, they earn a new set of 4 downs — a "first down."',
              'On 4th down, teams usually punt (kick the ball away) or attempt a field goal. Going for it on 4th down is risky but increasingly common in the modern NFL.',
              '"3rd and long" (needing 7+ yards) favors the defense. "3rd and short" (1-3 yards) favors the offense. This is why gaining yards on 1st and 2nd down is critical.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'rules-q1',
                type: 'multiple-choice',
                question: 'How many attempts (downs) does the offense get to move the ball 10 yards?',
                options: ['3', '4', '5', '6'],
                correctIndex: 1,
                explanation: 'The offense gets 4 downs to gain 10 yards. Achieve that and you get a fresh set of downs!'
              },
              {
                id: 'rules-q2',
                type: 'multiple-choice',
                question: 'What do most teams do on 4th down?',
                options: ['Always go for it', 'Punt or attempt a field goal', 'Take a timeout', 'Kneel the ball'],
                correctIndex: 1,
                explanation: 'On 4th down, teams typically punt to flip field position or kick a field goal if close enough. Going for it is risky but trending upward.'
              },
              {
                id: 'rules-q3',
                type: 'fix-mistake',
                question: '"If the offense fails to gain 10 yards in 4 downs, they get 2 extra downs." What\'s wrong?',
                options: ['Nothing, this is correct', 'There are no extra downs — the other team gets the ball (turnover on downs)', 'They get 1 extra down, not 2', 'The referee decides'],
                correctIndex: 1,
                explanation: 'If the offense fails to convert on 4th down, it\'s a turnover on downs — the defense takes over at that spot on the field.'
              }
            ]
          },
          {
            id: 'scoring',
            title: 'Scoring in Football',
            description: 'Touchdowns, field goals, safeties, and extra points.',
            conceptText: [
              'Touchdown = 6 points. Score by carrying or catching the ball into the end zone. After a TD, you attempt an extra point (1 pt kick) or a 2-point conversion (run/pass into the end zone).',
              'Field Goal = 3 points. The kicker boots the ball through the uprights. Typically attempted on 4th down when too far for a 1st down but close enough to kick.',
              'Safety = 2 points for the DEFENSE. Occurs when the offense is tackled in their own end zone. The offense also has to kick the ball to the other team afterward!'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'rules-q4',
                type: 'multiple-choice',
                question: 'How many points is a touchdown worth?',
                options: ['3', '5', '6', '7'],
                correctIndex: 2,
                explanation: 'A touchdown is worth 6 points. The extra point (kick) or 2-point conversion attempt comes after, potentially making it 7 or 8.'
              },
              {
                id: 'rules-q5',
                type: 'multiple-choice',
                question: 'What is a safety?',
                options: ['A 3-point score by the offense', 'A 2-point score when the offense is tackled in their own end zone', 'A type of defensive penalty', 'A deep defensive player'],
                correctIndex: 1,
                explanation: 'A safety awards 2 points to the defense when the ball carrier is tackled in their own end zone. The term also refers to the defensive position!'
              },
              {
                id: 'rules-q6',
                type: 'identify',
                question: 'After scoring a TD, a team lines up at the 2-yard line and runs the ball into the end zone. What did they just do?',
                options: ['Extra point kick', '2-point conversion', 'Onside kick', 'Fair catch kick'],
                correctIndex: 1,
                explanation: 'A 2-point conversion! Instead of kicking the extra point (1 pt), the team tries to get into the end zone from the 2-yard line for 2 points.'
              }
            ]
          },
          {
            id: 'game-clock',
            title: 'The Game Clock',
            description: 'Quarters, halftime, timeouts, and the 2-minute warning.',
            conceptText: [
              'An NFL game has 4 quarters of 15 minutes each (60 minutes total). Halftime separates the 2nd and 3rd quarters. The clock stops for incomplete passes, out-of-bounds, and timeouts.',
              'Each team gets 3 timeouts per half (6 total). Timeouts stop the clock and are crucial for late-game management — saving or burning them can decide the outcome.',
              'The 2-minute warning is an automatic timeout at 2:00 remaining in the 2nd and 4th quarters. It\'s like a free timeout that stops the clock for both teams.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'rules-q7',
                type: 'multiple-choice',
                question: 'How many timeouts does each team get per half?',
                options: ['2', '3', '4', '5'],
                correctIndex: 1,
                explanation: 'Each team gets 3 timeouts per half. They don\'t carry over — use them or lose them!'
              },
              {
                id: 'rules-q8',
                type: 'multiple-choice',
                question: 'What is the 2-minute warning?',
                options: ['A penalty for delay of game', 'An automatic timeout at 2:00 left in the 2nd and 4th quarters', 'A warning to the losing team', 'A signal to start overtime'],
                correctIndex: 1,
                explanation: 'The 2-minute warning is a built-in stoppage at the 2:00 mark in each half. It functions like a free timeout for both teams.'
              },
              {
                id: 'rules-q9',
                type: 'fix-mistake',
                question: '"The clock keeps running after an incomplete pass." What\'s wrong?',
                options: ['Nothing, this is correct', 'The clock STOPS after an incomplete pass', 'Only in the 4th quarter', 'The clock resets to 15 minutes'],
                correctIndex: 1,
                explanation: 'Incomplete passes always stop the clock. That\'s why teams throw to the sidelines in late-game situations — to save time!'
              }
            ]
          }
        ]
      },
      {
        id: 'penalties',
        title: 'Common Penalties',
        icon: '🚩',
        requiredXp: 30,
        lessons: [
          {
            id: 'pre-snap-penalties',
            title: 'Pre-Snap Penalties',
            description: 'False start, offsides, and encroachment.',
            conceptText: [
              'False Start (5 yards, offense): An offensive player moves before the ball is snapped. Only the center can move the ball, and only one player (in motion) can move laterally.',
              'Offsides (5 yards, defense): A defender is in the neutral zone when the ball is snapped. The play continues — it\'s a "free play" for the offense because the penalty is guaranteed.',
              'Encroachment (5 yards, defense): A defender crosses the line AND makes contact with an offensive player before the snap. Unlike offsides, the play is blown dead immediately.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'rules-q10',
                type: 'multiple-choice',
                question: 'What happens on a False Start penalty?',
                options: ['10-yard penalty on the defense', '5-yard penalty on the offense, play is dead', 'Loss of a timeout', 'The down is replayed with no penalty'],
                correctIndex: 1,
                explanation: 'False Start is a 5-yard penalty on the offense. The play is immediately blown dead — it doesn\'t get run.'
              },
              {
                id: 'rules-q11',
                type: 'multiple-choice',
                question: 'Why is an offsides penalty considered a "free play" for the offense?',
                options: ['The offense gets extra points', 'The play continues and the offense can take the result OR the penalty', 'The defense loses a player', 'The offense gets an extra down'],
                correctIndex: 1,
                explanation: 'On offsides, the play runs. The offense can take the penalty (5 yards) or the result of the play — whichever is better. It\'s risk-free!'
              },
              {
                id: 'rules-q12',
                type: 'fix-mistake',
                question: '"Encroachment and Offsides are the exact same penalty." What\'s wrong?',
                options: ['Nothing, they are the same', 'Encroachment involves contact and stops the play; Offsides lets the play continue', 'Offsides is 10 yards, Encroachment is 5', 'Only Encroachment exists in the NFL'],
                correctIndex: 1,
                explanation: 'Key difference: Encroachment means a defender touched an offensive player before the snap (play dead). Offsides just means they were in the neutral zone (play continues).'
              }
            ]
          },
          {
            id: 'in-play-penalties',
            title: 'In-Play Penalties',
            description: 'Holding, pass interference, and roughing the passer.',
            conceptText: [
              'Holding (10 yards, offense / 5 yards + auto first down, defense): Grabbing or restricting a player illegally. Offensive holding is the most called penalty in the NFL.',
              'Pass Interference (spot foul offense / spot foul + auto 1st down, defense): Illegally contacting a receiver while the ball is in the air. Defensive PI can be a HUGE penalty — it\'s enforced at the spot of the foul.',
              'Roughing the Passer (15 yards + auto first down): Hitting the QB after he\'s released the ball, hitting him in the head/neck area, or driving him into the ground. Protects the league\'s most important players.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'rules-q13',
                type: 'multiple-choice',
                question: 'How is Defensive Pass Interference enforced in the NFL?',
                options: ['Always 15 yards', 'Always 10 yards', 'Spot of the foul + automatic first down', '5 yards and replay the down'],
                correctIndex: 2,
                explanation: 'NFL DPI is a spot foul — enforced where it happened. A 50-yard DPI gives the offense 50 yards! It\'s one of the biggest penalties in football.'
              },
              {
                id: 'rules-q14',
                type: 'multiple-choice',
                question: 'What is the most frequently called penalty in the NFL?',
                options: ['Pass Interference', 'Roughing the Passer', 'Offensive Holding', 'False Start'],
                correctIndex: 2,
                explanation: 'Offensive holding is the most called penalty. Linemen are always battling pass rushers, and grabbing is an easy call for officials.'
              },
              {
                id: 'rules-q15',
                type: 'identify',
                question: 'A defender hits the QB in the helmet area well after the ball is thrown. The ref throws a flag. What penalty?',
                options: ['Pass Interference', 'Unnecessary Roughness', 'Roughing the Passer', 'Encroachment'],
                correctIndex: 2,
                explanation: 'Roughing the Passer! Hitting the QB after the throw AND in the head/neck area is a 15-yard penalty with an automatic first down.'
              }
            ]
          },
          {
            id: 'special-rules',
            title: 'Special Situations',
            description: 'Overtime, challenges, and the catch rule.',
            conceptText: [
              'NFL Overtime: Each team gets at least one possession UNLESS the first team scores a touchdown. Regular season OT is one 10-minute period (can end in a tie). Playoffs play until someone wins.',
              'Coach\'s Challenge: Each coach gets 2 challenges per game (3 if the first 2 are successful). They throw a red flag to challenge a call. A timeout is lost if the challenge fails.',
              'The Catch Rule: A receiver must (1) control the ball, (2) get two feet down (or another body part), and (3) make a "football move" or maintain control through going to the ground.'
            ],
            xpReward: 25,
            questions: [
              {
                id: 'rules-q16',
                type: 'multiple-choice',
                question: 'In NFL overtime, what happens if the first team kicks a field goal?',
                options: ['The game is over', 'The other team gets a possession', 'They play another full quarter', 'It goes to a coin flip'],
                correctIndex: 1,
                explanation: 'If the first team only scores a FG, the other team gets a chance to match or win. A first-possession TD ends it immediately though!'
              },
              {
                id: 'rules-q17',
                type: 'multiple-choice',
                question: 'How many feet must a receiver get down for a catch?',
                options: ['1 foot', '2 feet', '3 feet', 'No feet required'],
                correctIndex: 1,
                explanation: 'In the NFL, a receiver needs TWO feet in bounds (or one knee/elbow/etc.) for a completed catch. College only requires one foot!'
              },
              {
                id: 'rules-q18',
                type: 'fix-mistake',
                question: '"If a coach\'s challenge is unsuccessful, the team loses a timeout AND a down." What\'s wrong?',
                options: ['Nothing, this is correct', 'A failed challenge only costs a timeout, not a down', 'Challenges don\'t cost anything', 'Challenges cost 5 yards'],
                correctIndex: 1,
                explanation: 'A failed challenge costs one timeout — that\'s it. No loss of down. That\'s why coaches are careful about when to use them.'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'positions',
    difficulty: 'beginner',
    title: 'Player Positions',
    description: 'Learn every position on the field — what they do and why they matter.',
    color: 'accent',
    units: [
      {
        id: 'offense-positions',
        title: 'Offensive Positions',
        icon: '⭕',
        requiredXp: 0,
        lessons: [
          {
            id: 'pos-qb',
            title: 'Quarterback (QB)',
            description: 'The leader of the offense — the most important position in football.',
            conceptText: [
              'The Quarterback (QB) is the leader of the offense. They call plays in the huddle, read the defense at the line, and make pre-snap adjustments.',
              'On passing plays, the QB drops back, reads the coverage, and throws to open receivers. On running plays, they hand off to a running back or run it themselves.',
              'Great QBs combine arm strength, accuracy, football IQ, and leadership. They must read defenses in seconds and make split-second decisions under pressure.',
              'QBs line up either "under center" (directly behind the center) or in "shotgun" (5-7 yards back). Shotgun gives more time to read the defense; under center is better for play-action fakes.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q1',
                type: 'multiple-choice',
                question: 'What is the QB\'s primary responsibility on a passing play?',
                options: ['Identify the blitz and call an audible', 'Read the coverage post-snap and deliver the ball to the right receiver', 'Hold the ball as long as possible to let routes develop', 'Check down to the running back every time'],
                correctIndex: 1,
                explanation: 'The QB reads the defense post-snap, identifies open receivers based on the coverage, and delivers accurate throws in tight windows.'
              },
              {
                id: 'pos-q2',
                type: 'multiple-choice',
                question: 'What does it mean when a QB lines up in "shotgun"?',
                options: ['Standing behind the left tackle', '5-7 yards behind the center', 'On the sideline', 'Next to the running back'],
                correctIndex: 1,
                explanation: 'In shotgun, the QB stands 5-7 yards behind the center to receive a snap. This gives more time to read the defense before throwing.'
              },
              {
                id: 'pos-q3',
                type: 'fix-mistake',
                question: '"The QB\'s only job is to throw the ball." What\'s wrong with this statement?',
                options: ['Nothing, it\'s correct', 'QBs also read defenses, hand off, run, and make audibles', 'QBs only run the ball', 'QBs play defense too'],
                correctIndex: 1,
                explanation: 'QBs do far more than throw — they read defenses, call audibles, hand off to RBs, scramble, and lead the entire offense.'
              }
            ]
          },
          {
            id: 'pos-rb',
            title: 'Running Back (RB)',
            description: 'The versatile ball carrier who runs, catches, and blocks.',
            conceptText: [
              'The Running Back (RB) lines up behind or beside the QB. Their primary job is carrying the ball on running plays, finding holes in the defense and gaining yards.',
              'Modern RBs are also valuable receivers. They catch passes out of the backfield on screen plays, checkdowns, and route patterns — making them dual threats.',
              'RBs must also pass protect — blocking blitzing defenders to give the QB time to throw. A great RB excels at all three: running, catching, and blocking.',
              'There are different RB styles: Power backs (big, physical, short yardage), Speed backs (fast, explosive, outside runs), and All-purpose backs (do everything well).'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q4',
                type: 'multiple-choice',
                question: 'What are the three key skills of a modern running back?',
                options: ['Throwing, kicking, punting', 'Running, catching, blocking', 'Running, tackling, covering', 'Catching, blocking, punting'],
                correctIndex: 1,
                explanation: 'Modern RBs must run with the ball, catch passes out of the backfield, and block for the QB on pass plays.'
              },
              {
                id: 'pos-q5',
                type: 'identify',
                question: 'An RB stays in to block a blitzing linebacker instead of running a route. What is this called?',
                options: ['Screen play', 'Pass protection', 'Play-action', 'Audible'],
                correctIndex: 1,
                explanation: 'When an RB blocks a rusher to protect the QB, that\'s pass protection — a critical but underappreciated part of the position.'
              },
              {
                id: 'pos-q6',
                type: 'multiple-choice',
                question: 'Which type of RB is best suited for short-yardage situations near the goal line?',
                options: ['Speed back', 'Power back', 'Return specialist', 'Slot receiver'],
                correctIndex: 1,
                explanation: 'Power backs are bigger and more physical, making them ideal for short-yardage and goal-line situations where brute force matters.'
              }
            ]
          },
          {
            id: 'pos-wr',
            title: 'Wide Receiver (WR)',
            description: 'The speed demons who catch passes and stretch the field.',
            conceptText: [
              'Wide Receivers (WRs) line up on the outside edges of the formation. Their job is to run routes, get open, and catch passes from the QB.',
              'There are different WR positions: "X" (split end) lines up on the line of scrimmage, "Z" (flanker) lines up off the line, and the "Slot" lines up between the OL and outside WR.',
              'Route running is an art. Great WRs use precise footwork, head fakes, and changes of speed to create separation from defenders. Common routes include slants, outs, posts, and go routes.',
              'WRs also block on running plays! Perimeter blocking by WRs can turn a good run into a touchdown. It\'s unglamorous but essential.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q7',
                type: 'multiple-choice',
                question: 'What does the "Slot" receiver do?',
                options: ['Lines up outside the numbers', 'Lines up between the OL and outside WR', 'Plays on the defensive line', 'Only blocks on running plays'],
                correctIndex: 1,
                explanation: 'The slot receiver lines up inside, between the offensive line and the outside wide receiver. They often run shorter, quicker routes.'
              },
              {
                id: 'pos-q8',
                type: 'multiple-choice',
                question: 'Which of these is NOT a common wide receiver route?',
                options: ['Slant', 'Post', 'Blitz', 'Go route'],
                correctIndex: 2,
                explanation: 'A blitz is a defensive strategy, not a receiver route! Slants, posts, and go routes are all common passing routes.'
              },
              {
                id: 'pos-q9',
                type: 'fix-mistake',
                question: '"Wide receivers only need to catch the ball — blocking doesn\'t matter for them." What\'s wrong?',
                options: ['Nothing, WRs never block', 'WR blocking on run plays is essential and can spring big gains', 'WRs only play on passing downs', 'Blocking is only for linemen'],
                correctIndex: 1,
                explanation: 'WR blocking on the perimeter is crucial on running plays and screens. Good WR blocks can turn 5-yard gains into touchdowns.'
              }
            ]
          },
          {
            id: 'pos-te',
            title: 'Tight End (TE)',
            description: 'The hybrid player who blocks like a lineman and catches like a receiver.',
            conceptText: [
              'The Tight End (TE) is football\'s Swiss army knife. They line up next to the offensive tackle and can both block and run routes.',
              'As blockers, TEs help the run game by sealing the edge or double-teaming defensive linemen. A good blocking TE creates running lanes.',
              'As receivers, TEs run routes over the middle of the field. Their size (usually 6\'4"+, 240+ lbs) makes them mismatches against smaller linebackers and slower safeties.',
              'Modern TEs like Travis Kelce and George Kittle have revolutionized the position — they\'re elite receivers who also block. Teams build entire offenses around great TEs.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q10',
                type: 'multiple-choice',
                question: 'Why are tight ends considered "mismatches" in the passing game?',
                options: ['They\'re faster than all defenders', 'They\'re too big for LBs and too fast for safeties', 'They always line up in the slot', 'They throw the ball sometimes'],
                correctIndex: 1,
                explanation: 'TEs create mismatches because they\'re too big and physical for linebackers to cover, and too fast for most safeties.'
              },
              {
                id: 'pos-q11',
                type: 'identify',
                question: 'A TE lines up next to the right tackle and blocks the defensive end on a running play. What is this called?',
                options: ['Pass route', 'Inline blocking', 'Screen play', 'Shotgun snap'],
                correctIndex: 1,
                explanation: 'When a TE lines up on the line and blocks, that\'s inline blocking — helping create running lanes for the RB.'
              },
              {
                id: 'pos-q12',
                type: 'multiple-choice',
                question: 'Where do tight ends typically line up?',
                options: ['In the backfield behind the QB', 'Next to the offensive tackle on the line', 'Wide outside the numbers', 'Behind the defensive line'],
                correctIndex: 1,
                explanation: 'TEs traditionally line up on the line of scrimmage right next to the tackle, though modern offenses move them around.'
              }
            ]
          },
          {
            id: 'pos-ol',
            title: 'Offensive Line (OL)',
            description: 'The unsung heroes who protect the QB and open holes for runners.',
            conceptText: [
              'The Offensive Line (OL) consists of 5 players: Left Tackle (LT), Left Guard (LG), Center (C), Right Guard (RG), and Right Tackle (RT). They are the foundation of every play.',
              'On passing plays, the OL forms a "pocket" around the QB, blocking pass rushers. The left tackle protects the QB\'s blind side (for right-handed QBs) — it\'s often the second-most valuable position.',
              'On running plays, the OL creates holes by blocking defenders. They use techniques like "drive blocks" (pushing defenders back), "pull blocks" (pulling across the formation), and "double teams" (two linemen on one defender).',
              'The Center is unique — they snap the ball to start every play and must also call out blocking assignments. Great centers are like coaches on the field, reading the defense pre-snap.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'pos-q13',
                type: 'multiple-choice',
                question: 'Why is the left tackle position considered so important?',
                options: ['They run the ball the most', 'They protect the QB\'s blind side on pass plays', 'They catch the most passes', 'They call the plays'],
                correctIndex: 1,
                explanation: 'For a right-handed QB, the left tackle protects the blind side — the QB can\'t see rushers coming from there. A sack from the blind side often causes fumbles.'
              },
              {
                id: 'pos-q14',
                type: 'identify',
                question: 'A guard leaves his position and runs across the formation to block on the opposite side. What technique is this?',
                options: ['Drive block', 'Pull block', 'Screen block', 'Chop block'],
                correctIndex: 1,
                explanation: 'A pull block is when a lineman "pulls" out of their stance and runs laterally to block a defender on the other side — common in outside run plays.'
              },
              {
                id: 'pos-q15',
                type: 'multiple-choice',
                question: 'What unique responsibility does the Center have?',
                options: ['Throwing the ball', 'Snapping the ball and calling blocking assignments', 'Running routes', 'Covering receivers'],
                correctIndex: 1,
                explanation: 'The Center snaps the ball to start every play and identifies the defensive front to set blocking assignments for the rest of the OL.'
              }
            ]
          }
        ]
      },
      {
        id: 'defense-positions',
        title: 'Defensive Positions',
        icon: '❌',
        requiredXp: 50,
        lessons: [
          {
            id: 'pos-dl',
            title: 'Defensive Line (DL)',
            description: 'The front-line warriors who rush the passer and stop the run.',
            conceptText: [
              'The Defensive Line (DL) lines up across from the offensive line. Their job is to disrupt plays at the point of attack — rushing the passer and stopping the run.',
              'Defensive Ends (DEs) line up on the edges. They are usually the team\'s best pass rushers, using speed and technique to get around tackles and sack the QB.',
              'Defensive Tackles (DTs) line up in the interior. They must be massive and powerful to hold their ground against double teams and clog running lanes. The "Nose Tackle" lines up directly over the center.',
              'Great DL players can "two-gap" (control two gaps between OL) or "one-gap" (shoot through a single gap). Pass rush moves include the bull rush, swim move, spin move, and rip move.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q16',
                type: 'multiple-choice',
                question: 'What is the primary role of a Defensive End?',
                options: ['Absorb double teams in the interior to free up linebackers', 'Rush the passer from the edge using speed and technique', 'Drop into zone coverage on passing downs', 'Spy the quarterback on designed runs'],
                correctIndex: 1,
                explanation: 'DEs line up on the outside of the defensive line and are typically the team\'s best pass rushers, using speed and technique to attack from the edge.'
              },
              {
                id: 'pos-q17',
                type: 'identify',
                question: 'A defensive tackle lines up directly over the center and absorbs double teams to free up linebackers. What position is this?',
                options: ['Defensive End', 'Nose Tackle', 'Outside Linebacker', 'Safety'],
                correctIndex: 1,
                explanation: 'The Nose Tackle (NT) lines up over the center, occupying blockers so linebackers can make plays — especially important in 3-4 defenses.'
              },
              {
                id: 'pos-q18',
                type: 'multiple-choice',
                question: 'Which pass rush technique involves a defender driving straight into the blocker with power?',
                options: ['Swim move', 'Spin move', 'Bull rush', 'Rip move'],
                correctIndex: 2,
                explanation: 'The bull rush is a power technique where the rusher drives directly into the blocker, using strength to push them back into the QB.'
              }
            ]
          },
          {
            id: 'pos-lb',
            title: 'Linebacker (LB)',
            description: 'The quarterbacks of the defense — versatile playmakers.',
            conceptText: [
              'Linebackers (LBs) are the most versatile defenders. They line up behind the defensive line and must handle run defense, pass coverage, and blitzing.',
              'The Middle Linebacker (MLB/Mike) is the "quarterback of the defense." They call defensive signals, read the offense, and direct teammates before the snap.',
              'Outside Linebackers (OLBs) vary by scheme. In a 3-4 defense, OLBs often rush the passer. In a 4-3, they focus more on coverage and run support.',
              'Modern LBs need to cover running backs and tight ends in the passing game. The best LBs combine size, speed, instincts, and intelligence to be complete players.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q19',
                type: 'multiple-choice',
                question: 'Why is the middle linebacker called the "quarterback of the defense"?',
                options: ['They have the authority to call timeouts', 'They read the offense, call out assignments, and adjust the defense pre-snap', 'They line up directly across from the QB', 'They can throw the ball on trick plays'],
                correctIndex: 1,
                explanation: 'The MLB calls out defensive assignments, identifies offensive formations, and makes sure everyone is lined up correctly — leading the defense like a QB leads the offense.'
              },
              {
                id: 'pos-q20',
                type: 'multiple-choice',
                question: 'In a 3-4 defense, what role do outside linebackers often fill?',
                options: ['Covering slot receivers in man-to-man', 'Rushing the passer from the edge as stand-up pass rushers', 'Playing deep zone in Cover 3', 'Calling defensive audibles at the line'],
                correctIndex: 1,
                explanation: 'In a 3-4 defense (3 DL, 4 LBs), the OLBs often act as edge rushers, standing up and rushing the QB from the outside — similar to a DE in a 4-3.'
              },
              {
                id: 'pos-q21',
                type: 'fix-mistake',
                question: '"Linebackers only need to stop the run — they never cover receivers." What\'s wrong?',
                options: ['Nothing, LBs only play the run', 'Modern LBs must cover RBs and TEs in the passing game too', 'LBs play offense', 'LBs only blitz'],
                correctIndex: 1,
                explanation: 'Modern LBs must be able to cover tight ends and running backs in the passing game. A LB who can\'t cover is a liability in today\'s NFL.'
              }
            ]
          },
          {
            id: 'pos-db',
            title: 'Defensive Backs (CBs & Safeties)',
            description: 'The last line of defense — covering receivers and preventing big plays.',
            conceptText: [
              'Defensive Backs (DBs) are the secondary — Cornerbacks (CBs) and Safeties (S). They are the last line of defense against the pass and must prevent big plays.',
              'Cornerbacks (CBs) primarily cover wide receivers. They play man-to-man or zone coverage and need elite speed, agility, and ball skills. Great CBs "shadow" the opponent\'s best receiver.',
              'Free Safeties (FS) play deep, reading the QB\'s eyes and breaking on the ball. They\'re the "center fielder" of the defense. Strong Safeties (SS) play closer to the line and are better in run support.',
              'DBs use techniques like "press coverage" (jamming receivers at the line), "off coverage" (giving a cushion), and "trail technique" (staying behind in man coverage). Ball skills — the ability to intercept — separate good DBs from great ones.'
            ],
            xpReward: 20,
            questions: [
              {
                id: 'pos-q22',
                type: 'multiple-choice',
                question: 'What is the "center fielder" of a defense?',
                options: ['Middle Linebacker', 'Free Safety', 'Cornerback', 'Nose Tackle'],
                correctIndex: 1,
                explanation: 'The Free Safety plays deep, reads the QB, and covers the middle of the field — like a center fielder in baseball covering the deepest part of the field.'
              },
              {
                id: 'pos-q23',
                type: 'identify',
                question: 'A cornerback lines up right at the line of scrimmage and jams the receiver at the snap. What technique is this?',
                options: ['Off coverage', 'Zone drop', 'Press coverage', 'Spy technique'],
                correctIndex: 2,
                explanation: 'Press coverage means the CB lines up tight to the receiver and uses a physical jam at the line to disrupt their route timing.'
              },
              {
                id: 'pos-q24',
                type: 'multiple-choice',
                question: 'What is the main difference between a Free Safety and a Strong Safety?',
                options: ['FS plays deep and reads the QB; SS plays closer to the line for run support', 'FS is bigger; SS is faster', 'FS covers TEs; SS covers WRs', 'There is no difference'],
                correctIndex: 0,
                explanation: 'The FS plays deep as a "last line" pass defender, while the SS lines up closer to the line of scrimmage and is more involved in run defense and covering TEs.'
              }
            ]
          }
        ]
      },
      {
        id: 'special-teams-positions',
        title: 'Special Teams',
        icon: '⚡',
        requiredXp: 100,
        lessons: [
          {
            id: 'pos-st',
            title: 'Kickers, Punters & Returners',
            description: 'The specialists who handle kicks, punts, and field position.',
            conceptText: [
              'Special teams are the units that play during kicks, punts, and field goals. They\'re often overlooked but can decide close games. "Special teams wins championships" is a coaching cliché for a reason.',
              'The Kicker (K) handles kickoffs and field goal attempts. The Punter (P) kicks the ball away on 4th down to pin the opponent deep. Both need consistency under pressure.',
              'The Kick Returner (KR) and Punt Returner (PR) catch kicks and try to gain yards. Elite returners with speed and vision can flip field position or score touchdowns.',
              'The Long Snapper (LS) is a specialist who snaps the ball on punts and field goals. It\'s a small role but critical — a bad snap can ruin a kick and change a game.'
            ],
            xpReward: 15,
            questions: [
              {
                id: 'pos-q25',
                type: 'multiple-choice',
                question: 'When does the punter come onto the field?',
                options: ['Every play', 'On 4th down when the team decides to kick the ball away', 'Only during overtime', 'On 3rd and long'],
                correctIndex: 1,
                explanation: 'The punter kicks the ball on 4th down to push the opponent back and improve field position, rather than risking a turnover on downs.'
              },
              {
                id: 'pos-q26',
                type: 'multiple-choice',
                question: 'What does a Long Snapper do?',
                options: ['Throws long passes', 'Snaps the ball on punts and field goals', 'Returns kicks', 'Blocks for the kicker'],
                correctIndex: 1,
                explanation: 'The Long Snapper delivers a precise snap 7-15 yards to the holder (on FGs) or punter. Accuracy is everything — a bad snap can be disastrous.'
              },
              {
                id: 'pos-q27',
                type: 'fix-mistake',
                question: '"Special teams don\'t really matter — they barely affect the game." What\'s wrong?',
                options: ['Nothing, special teams are unimportant', 'Special teams impact field position, scoring, and momentum — they can decide games', 'Special teams only matter in the playoffs', 'Only the kicker matters'],
                correctIndex: 1,
                explanation: 'Special teams affect field position on every possession, directly score points on FGs, and return TDs can swing momentum. Many games are decided by special teams plays.'
              }
            ]
          }
        ]
      }
    ]
  }
  ,
  // ── Schemes & Systems ──────────────────────────────────────────
  {
    id: 'schemes-systems',
    difficulty: 'advanced',
    title: 'Schemes & Systems',
    description: 'Learn the history, philosophy, and mechanics behind football\'s most influential offensive and defensive systems.',
    color: 'primary',
    units: [
      {
        id: 'west-coast-offense',
        title: 'West Coast Offense',
        icon: '🌉',
        requiredXp: 0,
        lessons: [
          {
            id: 'wco-origins',
            title: 'Origins of the West Coast Offense',
            description: 'How Bill Walsh revolutionized football with short, precise passing.',
            xpReward: 30,
            conceptText: [
              'The West Coast Offense was developed by Bill Walsh while he was an assistant coach with the Cincinnati Bengals in the late 1960s and refined when he became head coach of the San Francisco 49ers in 1979.',
              'Walsh\'s innovation came out of necessity — his quarterbacks lacked elite arm strength, so he designed a system that used short, timed passes to replace the running game and control the ball.',
              'The core philosophy: "The pass sets up the run." Instead of pounding the ball on the ground to open up play-action, Walsh threw quick, high-percentage passes to move the chains, then attacked with the run when defenses spread out.',
              'Joe Montana became the perfect quarterback for this system — cerebral, accurate, and decisive. Together, Walsh and Montana won three Super Bowls and changed the way offense was played forever.'
            ],
            questions: [
              {
                id: 'wco-origins-q1',
                type: 'multiple-choice',
                question: 'Who is credited with creating the West Coast Offense?',
                options: ['Don Coryell', 'Bill Walsh', 'Bill Belichick', 'Vince Lombardi'],
                correctIndex: 1,
                explanation: 'Bill Walsh developed the West Coast Offense during his time with the Bengals and perfected it with the 49ers, winning three Super Bowls.'
              },
              {
                id: 'wco-origins-q2',
                type: 'multiple-choice',
                question: 'What is the core philosophy of the West Coast Offense?',
                options: ['Run the ball 40+ times per game', 'The pass sets up the run', 'Always throw deep on first down', 'Use only shotgun formations'],
                correctIndex: 1,
                explanation: 'Unlike traditional offenses where the run sets up the pass, Walsh flipped the script: short, high-percentage passes move the chains and open up running lanes.'
              },
              {
                id: 'wco-origins-q3',
                type: 'fix-mistake',
                question: '"The West Coast Offense was designed around having a strong-armed quarterback who could throw deep." What\'s wrong?',
                options: ['Nothing, it requires a cannon arm', 'It was designed for accurate, cerebral QBs who excelled at short, timed passes', 'It was designed only for running QBs', 'It requires two quarterbacks'],
                correctIndex: 1,
                explanation: 'Walsh designed the WCO specifically because his quarterbacks lacked elite arm strength. The system relies on accuracy, timing, and quick decisions rather than deep throws.'
              }
            ]
          },
          {
            id: 'wco-mechanics',
            title: 'How the West Coast Offense Works',
            description: 'Short timing routes, YAC, and the rhythm passing game.',
            xpReward: 30,
            conceptText: [
              'The WCO is built on timing. The quarterback takes a 3- or 5-step drop and releases the ball on schedule — before the receiver even looks back. This reduces sack opportunities and pressures the defense to react fast.',
              'Key route concepts include slants, drags, out routes, and crossing patterns. These are designed to get the ball to playmakers in space quickly, relying on yards after catch (YAC) to generate big gains.',
              'The offense uses a complex system of reads. The QB surveys the field pre-snap, identifies the coverage, and knows exactly where to go with the ball based on how the defense aligns.',
              'Play-action becomes devastating in the WCO because defenses must respect the run once short passes have been completed consistently. Suddenly, deep shots open up behind safeties who have crept forward.'
            ],
            questions: [
              {
                id: 'wco-mech-q1',
                type: 'multiple-choice',
                question: 'What does YAC stand for in football?',
                options: ['Yards After Contact', 'Yards After Catch', 'Your Adjusted Completion', 'Yards Against Coverage'],
                correctIndex: 1,
                explanation: 'Yards After Catch (YAC) measures how many yards a receiver gains after catching the ball. The WCO is designed to maximize YAC by getting the ball to players in space.'
              },
              {
                id: 'wco-mech-q2',
                type: 'identify',
                question: 'Which route concept is most associated with the West Coast Offense?',
                options: ['Hail Mary deep bombs', 'Short timing routes like slants and drags', 'Screen passes only', 'Trick plays and reverses'],
                correctIndex: 1,
                explanation: 'The WCO revolves around short, timed routes — slants, drags, outs, and crossers that get the ball out quickly and let receivers create YAC.'
              },
              {
                id: 'wco-mech-q3',
                type: 'multiple-choice',
                question: 'Why is play-action so effective in the West Coast Offense?',
                options: ['Because the QB runs the ball often', 'Because short passes force safeties to creep up, opening deep shots', 'Because they never run play-action', 'Because the offensive line is always pulling'],
                correctIndex: 1,
                explanation: 'After completing many short passes, defenders cheat forward to jump routes. Play-action freezes them, creating deep windows behind the secondary.'
              }
            ]
          }
        ]
      },
      {
        id: 'air-raid-offense',
        title: 'Air Raid Offense',
        icon: '✈️',
        requiredXp: 75,
        lessons: [
          {
            id: 'air-raid-origins',
            title: 'The Birth of the Air Raid',
            description: 'How Hal Mumme and Mike Leach changed football with four wide receivers.',
            xpReward: 30,
            conceptText: [
              'The Air Raid offense was popularized by Hal Mumme and Mike Leach at small programs like Iowa Wesleyan and Valdosta State in the late 1980s and early 1990s.',
              'The philosophy was simple but radical: spread the field with four wide receivers, throw the ball on nearly every play, and use a simplified route tree that allows receivers to read the defense and adjust on the fly.',
              'Mike Leach took the Air Raid to Texas Tech and later Washington State, consistently producing top-tier passing offenses with players who weren\'t top recruits. The system made average players dangerous.',
              'The Air Raid influenced the modern NFL through coaches like Kliff Kingsbury and Lincoln Riley, and concepts like the "mesh" route combo have become ubiquitous at every level of football.'
            ],
            questions: [
              {
                id: 'air-raid-q1',
                type: 'multiple-choice',
                question: 'Who are the two coaches most credited with developing the Air Raid offense?',
                options: ['Bill Walsh and Joe Montana', 'Hal Mumme and Mike Leach', 'Nick Saban and Dabo Swinney', 'Andy Reid and Sean McVay'],
                correctIndex: 1,
                explanation: 'Hal Mumme and Mike Leach developed the Air Raid at small college programs, proving that a pass-first system with simplified reads could work anywhere.'
              },
              {
                id: 'air-raid-q2',
                type: 'multiple-choice',
                question: 'How many wide receivers does the Air Raid typically use?',
                options: ['Two', 'Three', 'Four', 'Five'],
                correctIndex: 2,
                explanation: 'The Air Raid spreads the field with four wide receivers to create space and mismatches, forcing defenses into sub packages.'
              },
              {
                id: 'air-raid-q3',
                type: 'identify',
                question: 'What makes the Air Raid\'s route tree unique?',
                options: ['Routes are extremely complex and require years to learn', 'Receivers read the defense and adjust routes on the fly', 'Only deep routes are used', 'Receivers never change their routes'],
                correctIndex: 1,
                explanation: 'The Air Raid uses a simplified route tree where receivers read coverage and adjust their routes accordingly, creating a built-in answer for every defensive look.'
              }
            ]
          },
          {
            id: 'air-raid-concepts',
            title: 'Air Raid Route Concepts',
            description: 'Mesh, Y-Cross, and other staple Air Raid plays.',
            xpReward: 30,
            conceptText: [
              'The Mesh concept is the signature Air Raid play. Two inside receivers run shallow crossing routes in opposite directions, creating natural "picks" that rub off man coverage defenders.',
              'The Y-Cross (or "Y-Sail") sends a receiver on a deep crossing route behind the linebackers while other routes clear out underneath coverage. It attacks Cover 3 and Cover 4 beautifully.',
              'Four Verticals is another Air Raid staple — all four receivers run straight up the field, stretching the defense vertically and creating windows between zone defenders.',
              'What makes these concepts powerful is their versatility. The same play call can attack man or zone coverage because receivers adjust their routes based on what the defense shows them.'
            ],
            questions: [
              {
                id: 'air-raid-con-q1',
                type: 'identify',
                question: 'What is the signature route concept of the Air Raid offense?',
                options: ['Hail Mary', 'Mesh', 'Spider 2 Y Banana', 'Power Run'],
                correctIndex: 1,
                explanation: 'The Mesh concept — two shallow crossing routes creating natural rubs — is the most iconic Air Raid play and has spread throughout all levels of football.'
              },
              {
                id: 'air-raid-con-q2',
                type: 'multiple-choice',
                question: 'What coverage does the Y-Cross concept particularly exploit?',
                options: ['Cover 0 Blitz', 'Cover 3 and Cover 4', 'Prevent defense', 'Goal line defense'],
                correctIndex: 1,
                explanation: 'The deep crossing route in Y-Cross attacks the seams between zone defenders in Cover 3 and Cover 4, finding open windows behind linebackers.'
              },
              {
                id: 'air-raid-con-q3',
                type: 'fix-mistake',
                question: '"Four Verticals only works against man coverage." What\'s wrong?',
                options: ['Nothing, it only beats man', 'Four Verticals stretches zone defenders vertically, creating windows between them', 'Four Verticals is a running play', 'It only works in the red zone'],
                correctIndex: 1,
                explanation: 'Four Verticals is actually designed to attack zone coverage by sending four receivers deep and stretching the defense vertically, forcing zone defenders to choose who to cover.'
              }
            ]
          }
        ]
      },
      {
        id: 'spread-offense',
        title: 'Spread Offense',
        icon: '🏃',
        requiredXp: 150,
        lessons: [
          {
            id: 'spread-evolution',
            title: 'The Spread Revolution',
            description: 'How spreading the field changed football from high school to the NFL.',
            xpReward: 30,
            conceptText: [
              'The Spread offense isn\'t a single system — it\'s a philosophy. The idea: align receivers wide to spread the defense horizontally, creating space in the box for the run game and isolating defenders in coverage.',
              'Rich Rodriguez popularized the spread-option at West Virginia, using a mobile quarterback as a runner. Urban Meyer\'s spread at Florida and Ohio State added RPOs (run-pass options) that made defenses wrong no matter what they chose.',
              'The spread forced defenses to evolve. The nickel package (5 DBs) went from a specialty sub-package to a base defense. Linebackers got smaller and faster. Edge rushers became hybrid players.',
              'In the NFL, coaches like Chip Kelly, Sean McVay, and Kyle Shanahan adapted spread concepts, using pre-snap motion, RPOs, and condensed formations to create the same spacing advantages with pro-level talent.'
            ],
            questions: [
              {
                id: 'spread-q1',
                type: 'multiple-choice',
                question: 'What is the primary goal of spread formations?',
                options: ['Put all players close together', 'Spread defenders horizontally to create space', 'Eliminate the passing game', 'Use only heavy personnel'],
                correctIndex: 1,
                explanation: 'By aligning receivers wide, the spread forces defenders to cover more space, creating running lanes in the box and isolating defenders in coverage.'
              },
              {
                id: 'spread-q2',
                type: 'multiple-choice',
                question: 'What does RPO stand for?',
                options: ['Run Pass Option', 'Right Player Offense', 'Rapid Play Operation', 'Receiver Position Order'],
                correctIndex: 0,
                explanation: 'RPO stands for Run-Pass Option. The quarterback reads a defender post-snap and decides whether to hand off, throw a quick pass, or keep the ball based on that defender\'s reaction.'
              },
              {
                id: 'spread-q3',
                type: 'identify',
                question: 'How did the spread offense change defensive personnel?',
                options: ['Defenses got bigger and slower', 'Nickel (5 DBs) became a base defense, LBs got smaller and faster', 'No changes were needed', 'Defenses added more linemen'],
                correctIndex: 1,
                explanation: 'The spread forced defenses to match speed with speed. The nickel package became standard, linebackers got more athletic, and hybrid players emerged.'
              }
            ]
          },
          {
            id: 'spread-rpo',
            title: 'RPOs: The Spread\'s Secret Weapon',
            description: 'How run-pass options put defenders in impossible positions.',
            xpReward: 30,
            conceptText: [
              'An RPO gives the quarterback a post-snap read on a specific defender. The offensive line blocks as if it\'s a run play, while one or more receivers run a quick pass route.',
              'If the read defender crashes down to stop the run, the QB pulls the ball and throws the quick pass. If the defender drops into coverage, the QB hands off. The defense is wrong either way.',
              'There are three main types of RPOs: pre-snap RPOs (based on alignment), post-snap glance RPOs (quick bubble or slant reads), and full-field RPOs (where the QB can progress through multiple options).',
              'RPOs have become controversial because offensive linemen are technically "blocking downfield" before the pass — which is illegal. The NFL allows 1 yard of downfield blocking; college allows 3 yards. This gray area is constantly debated.'
            ],
            questions: [
              {
                id: 'rpo-q1',
                type: 'multiple-choice',
                question: 'In an RPO, what determines whether the QB hands off or throws?',
                options: ['A coin flip', 'The read defender\'s reaction after the snap', 'The coach signals from the sideline', 'The weather conditions'],
                correctIndex: 1,
                explanation: 'The QB reads a specific defender post-snap. If that defender crashes toward the run, the QB throws. If the defender covers the pass, the QB hands off.'
              },
              {
                id: 'rpo-q2',
                type: 'fix-mistake',
                question: '"In an RPO, the offensive line pass-blocks while the QB decides to run or throw." What\'s wrong?',
                options: ['Nothing, that\'s correct', 'The offensive line run-blocks, while receivers run pass routes — that\'s what makes it an RPO', 'The offensive line doesn\'t block at all', 'Only the center blocks'],
                correctIndex: 1,
                explanation: 'The key to an RPO is that the offensive line blocks for a run while receivers run routes. This creates the illusion of a run play and puts the read defender in conflict.'
              },
              {
                id: 'rpo-q3',
                type: 'multiple-choice',
                question: 'Why are RPOs controversial in the NFL?',
                options: ['They\'re too easy to defend', 'Offensive linemen may be illegally downfield before the pass', 'Quarterbacks get hurt too often', 'Receivers don\'t like running routes'],
                correctIndex: 1,
                explanation: 'Since the O-line blocks for a run, they often get beyond the 1-yard NFL limit downfield before the QB throws, creating a gray area that officials must judge in real time.'
              }
            ]
          }
        ]
      },
      {
        id: 'defensive-systems',
        title: 'Legendary Defensive Systems',
        icon: '🛡️',
        requiredXp: 200,
        lessons: [
          {
            id: 'tampa-2',
            title: 'Tampa 2 Defense',
            description: 'Tony Dungy\'s Cover 2 innovation that dominated the NFL.',
            xpReward: 30,
            conceptText: [
              'The Tampa 2 was popularized by Tony Dungy and Monte Kiffin with the Tampa Bay Buccaneers in the late 1990s. It\'s a variation of Cover 2 zone defense with one critical tweak.',
              'In traditional Cover 2, two safeties split the deep field in half and the middle linebacker covers the short middle zone. In the Tampa 2, the MLB drops deep to cover the seam between the two safeties — essentially creating a Cover 3 look in the deep third.',
              'This requires a special kind of middle linebacker — fast enough to run with tight ends and slot receivers downfield, smart enough to read the quarterback\'s eyes, and disciplined enough to drop deep on every play.',
              'The Tampa 2\'s weakness is the flat areas and deep sidelines. Offenses learned to attack the corners of the field and use quick timing routes before the zones settled. Still, when executed well, it was nearly impossible to throw over the top.'
            ],
            questions: [
              {
                id: 'tampa2-q1',
                type: 'multiple-choice',
                question: 'What makes the Tampa 2 different from standard Cover 2?',
                options: ['More blitzing', 'The MLB drops deep to cover the seam between safeties', 'It uses man coverage', 'The safeties play closer to the line'],
                correctIndex: 1,
                explanation: 'The Tampa 2\'s innovation is the middle linebacker dropping into a deep zone, effectively creating three deep defenders instead of two and eliminating the deep middle vulnerability.'
              },
              {
                id: 'tampa2-q2',
                type: 'identify',
                question: 'What type of middle linebacker does the Tampa 2 require?',
                options: ['A huge run-stuffer who stays near the line', 'A fast, rangy LB who can drop deep and cover receivers', 'A blitzing specialist', 'Any linebacker can play it'],
                correctIndex: 1,
                explanation: 'The Tampa 2 requires an athletic MLB like Derrick Brooks or Brian Urlacher — someone who can cover ground, run with tight ends, and read the QB\'s eyes while dropping deep.'
              },
              {
                id: 'tampa2-q3',
                type: 'fix-mistake',
                question: '"The Tampa 2 has no weaknesses because it covers every part of the field." What\'s wrong?',
                options: ['Nothing, it\'s a perfect defense', 'The flats and deep sidelines are vulnerable to quick timing routes', 'It can\'t stop the run', 'It requires 12 players on the field'],
                correctIndex: 1,
                explanation: 'The Tampa 2\'s zones take time to settle, making it vulnerable to quick passes in the flats and along the sidelines. Offenses exploit the corners of the field against it.'
              }
            ]
          },
          {
            id: '46-defense-history',
            title: 'The 46 Defense',
            description: 'Buddy Ryan\'s aggressive scheme that powered the \'85 Bears.',
            xpReward: 30,
            conceptText: [
              'The 46 Defense was created by Buddy Ryan and made famous by the 1985 Chicago Bears — widely considered the greatest defense in NFL history. It\'s named after safety Doug Plank, who wore #46.',
              'The 46 loads the line of scrimmage with six or more defenders in the box. Both safeties play near the line, and the defense dares the offense to pass against tight man coverage with minimal safety help.',
              'The philosophy is pure aggression: overwhelm the offense with pressure from every angle. Blitzes come from linebackers, safeties, and cornerbacks. The goal is to hit the quarterback before he can find open receivers.',
              'The 46\'s weakness is obvious — with safeties playing up, one missed tackle or blown coverage results in a long touchdown. The 1985 Bears had such dominant personnel (Mike Singletary, Richard Dent, Dan Hampton) that they could afford the risk.'
            ],
            questions: [
              {
                id: '46-q1',
                type: 'multiple-choice',
                question: 'The 46 Defense is named after:',
                options: ['The number of players on defense', 'Safety Doug Plank\'s jersey number (#46)', 'The year it was invented (1946)', '46 different blitz packages'],
                correctIndex: 1,
                explanation: 'The 46 Defense got its name from safety Doug Plank, who wore #46. Buddy Ryan designed the defense around Plank\'s aggressive, physical style of play.'
              },
              {
                id: '46-q2',
                type: 'multiple-choice',
                question: 'What is the core philosophy of the 46 Defense?',
                options: ['Play soft coverage and wait for mistakes', 'Overwhelm the offense with constant pressure from every angle', 'Use zone coverage with deep safeties', 'Rely on turnovers from the secondary'],
                correctIndex: 1,
                explanation: 'The 46 is built on relentless aggression — loading the box, blitzing from everywhere, and pressuring the QB before he can throw. It trades safety help for overwhelming pass rush.'
              },
              {
                id: '46-q3',
                type: 'identify',
                question: 'What is the biggest vulnerability of the 46 Defense?',
                options: ['It can\'t stop the run', 'With safeties near the line, one blown coverage allows a long touchdown', 'It requires too many players', 'The defensive line can\'t rush the passer'],
                correctIndex: 1,
                explanation: 'Because the 46 commits safeties to the box, there\'s minimal help over the top. One mistake in man coverage or a missed tackle can result in a big play for the offense.'
              }
            ]
          },
          {
            id: 'zone-blitz',
            title: 'Zone Blitz: Dick LeBeau\'s Innovation',
            description: 'How dropping linemen into coverage changed the blitz forever.',
            xpReward: 30,
            conceptText: [
              'The Zone Blitz was pioneered by Dick LeBeau with the Pittsburgh Steelers in the 1990s and 2000s. It solved the fundamental problem with blitzing: leaving defenders in man coverage without safety help.',
              'The concept is deceptively simple: send a linebacker or defensive back on a blitz, but drop a defensive lineman into a short zone where the blitzer vacated. The offense sees pressure but throws into an unexpected zone defender.',
              'Quarterbacks who read "blitz" pre-snap expect man coverage and hot routes to open areas. Instead, they find a 280-pound defensive end standing in the passing lane, ready to intercept or bat down the throw.',
              'The Zone Blitz remains one of the most important innovations in defensive football. It allows coordinators to bring pressure without sacrificing coverage, and it creates confusion for even the most experienced quarterbacks.'
            ],
            questions: [
              {
                id: 'zb-q1',
                type: 'multiple-choice',
                question: 'Who is credited with popularizing the Zone Blitz?',
                options: ['Bill Belichick', 'Dick LeBeau', 'Buddy Ryan', 'Tony Dungy'],
                correctIndex: 1,
                explanation: 'Dick LeBeau developed and popularized the Zone Blitz with the Pittsburgh Steelers, creating one of the most influential defensive innovations in NFL history.'
              },
              {
                id: 'zb-q2',
                type: 'identify',
                question: 'What makes the Zone Blitz different from a traditional blitz?',
                options: ['More players rush the QB', 'A lineman drops into zone coverage where the blitzer left', 'The defense lines up differently', 'It only works on third down'],
                correctIndex: 1,
                explanation: 'The Zone Blitz replaces the blitzing player with a zone defender (often a DE dropping into coverage), maintaining coverage integrity while still bringing pressure.'
              },
              {
                id: 'zb-q3',
                type: 'fix-mistake',
                question: '"When a QB sees a blitz, he should throw hot to the area the blitzer vacated — this always works." What\'s wrong?',
                options: ['That\'s correct, hot routes always beat blitzes', 'In a Zone Blitz, a defender drops into that exact area, turning the hot throw into a potential interception', 'The QB should always run when blitzed', 'Hot routes don\'t exist'],
                correctIndex: 1,
                explanation: 'The Zone Blitz is specifically designed to bait the QB into throwing hot to a vacated area — except a defensive lineman has dropped into zone coverage right there, creating a trap.'
              }
            ]
          }
        ]
      },
      {
        id: 'modern-nfl-systems',
        title: 'Modern NFL Systems',
        icon: '🏟️',
        requiredXp: 300,
        lessons: [
          {
            id: 'shanahan-system',
            title: 'The Shanahan Coaching Tree',
            description: 'Outside zone, bootlegs, and the scheme that won multiple Super Bowls.',
            xpReward: 35,
            conceptText: [
              'Mike Shanahan built a dynasty in Denver around the outside zone running scheme. His son Kyle refined it into one of the most sophisticated offensive systems in football, winning a Super Bowl with the 49ers.',
              'The outside zone asks every offensive lineman to step laterally and create a wall. The running back reads the blocks and cuts to daylight. It doesn\'t require dominant individual blockers — it\'s a team concept.',
              'Kyle Shanahan added pre-snap motion, play-action bootlegs, and misdirection to his father\'s foundation. The 49ers\' offense uses heavy personnel (fullbacks, tight ends) but still spreads the field through motion.',
              'The coaching tree extends to Matt LaFleur (Packers), Kevin O\'Connell (Vikings), and Robert Saleh (formerly Jets defense). This system\'s influence on modern football is massive.'
            ],
            questions: [
              {
                id: 'shan-q1',
                type: 'multiple-choice',
                question: 'What running scheme is the Shanahan system built around?',
                options: ['Power run', 'Inside zone', 'Outside zone', 'Counter trey'],
                correctIndex: 2,
                explanation: 'The Shanahan system is built on outside zone runs, where the offensive line creates a lateral wall and the running back reads blocks to find the cutback lane.'
              },
              {
                id: 'shan-q2',
                type: 'multiple-choice',
                question: 'What did Kyle Shanahan add to his father\'s offensive system?',
                options: ['Eliminated the running game', 'Pre-snap motion, play-action bootlegs, and misdirection', 'Only shotgun formations', 'A no-huddle system'],
                correctIndex: 1,
                explanation: 'Kyle Shanahan evolved the system by adding extensive pre-snap motion, play-action bootlegs that get the QB on the move, and misdirection to manipulate defensive eyes.'
              },
              {
                id: 'shan-q3',
                type: 'identify',
                question: 'Why doesn\'t outside zone require dominant individual blockers?',
                options: ['Because no one blocks', 'It\'s a team concept where all linemen step laterally to create a wall', 'The QB runs instead', 'Only the center blocks'],
                correctIndex: 1,
                explanation: 'Outside zone is a collaborative scheme — every lineman steps in the same direction, creating a moving wall. The back reads the blocks and finds the cutback lane, so no single player needs to dominate.'
              }
            ]
          },
          {
            id: 'mcvay-revolution',
            title: 'The McVay Effect',
            description: 'How Sean McVay changed the NFL with pre-snap motion and tempo.',
            xpReward: 35,
            conceptText: [
              'When Sean McVay became the youngest head coach in modern NFL history with the LA Rams in 2017, he immediately transformed one of the league\'s worst offenses into one of its best.',
              'McVay\'s system relies heavily on pre-snap motion to diagnose coverage before the snap. By sending a receiver in motion, the defense reveals whether it\'s playing man or zone based on how defenders react.',
              'The "McVay Effect" goes beyond just his team. His coaching tree and imitators spread across the league — 11-personnel (1 RB, 1 TE, 3 WR), heavy use of play-action, and compressed formations that create easy throws.',
              'McVay also proved that offensive innovation could be a force multiplier. Jared Goff — a QB with physical tools but limited processing ability — looked like an MVP candidate because the system simplified his reads and gave him easy completions.'
            ],
            questions: [
              {
                id: 'mcvay-q1',
                type: 'multiple-choice',
                question: 'How does pre-snap motion help the quarterback?',
                options: ['It confuses the offense', 'It reveals whether the defense is in man or zone coverage', 'It adds a 12th player', 'It slows down the game'],
                correctIndex: 1,
                explanation: 'When a receiver goes in motion, man defenders follow while zone defenders stay put. This tells the QB what coverage he\'s facing before the snap, simplifying his post-snap reads.'
              },
              {
                id: 'mcvay-q2',
                type: 'multiple-choice',
                question: 'What does 11-personnel mean?',
                options: ['11 players on the field', '1 running back, 1 tight end, 3 wide receivers', '1 quarterback, 1 receiver', '11 linemen'],
                correctIndex: 1,
                explanation: '11-personnel refers to 1 RB, 1 TE, and 3 WR on the field. It\'s the most common personnel grouping in the modern NFL and is central to the McVay system.'
              },
              {
                id: 'mcvay-q3',
                type: 'fix-mistake',
                question: '"Pre-snap motion is just for show — it doesn\'t give the QB any real information." What\'s wrong?',
                options: ['That\'s correct, motion is aesthetic only', 'Motion is a diagnostic tool that reveals defensive coverage and alignment', 'Motion is only used in college', 'Motion confuses the offensive players'],
                correctIndex: 1,
                explanation: 'Pre-snap motion is one of the most valuable tools in modern football. It reveals coverage, creates alignment advantages, and gives the QB a pre-snap answer for where to throw.'
              }
            ]
          }
        ]
      }
    ]
  },
  // ── New: Game Management (Intermediate) ──────────────────
  {
    id: 'game-management',
    difficulty: 'intermediate' as DifficultyLevel,
    title: 'Game Management',
    description: 'Clock control, situational awareness, and in-game strategy.',
    color: 'primary',
    units: [
      {
        id: 'clock-management',
        title: 'Clock Management',
        icon: '⏱️',
        requiredXp: 150,
        lessons: [
          {
            id: 'fb-clock-basics',
            title: 'Controlling the Clock',
            description: 'Why time is a weapon in football.',
            conceptText: [
              'The play clock (40 seconds) and game clock are two different things. Smart teams use both to control the pace of the game.',
              'Running plays keep the clock moving. Incomplete passes and out-of-bounds plays stop it. A team with the lead wants to run the clock; a trailing team wants to stop it.',
              'Two-minute drills flip the script — the offense hurries to the line, uses no-huddle, and throws to the sidelines. Clock management wins and loses games in the NFL.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'fb-clock-sit1',
                type: 'situational' as const,
                question: 'What should the offense do?',
                situationContext: '2:00 left in 4th quarter | Own 35-yard line | Leading by 3',
                options: [
                  'Throw deep to score again',
                  'Run the ball and keep the clock moving',
                  'Call timeout to discuss strategy',
                  'Attempt a field goal',
                ],
                correctIndex: 1,
                explanation: 'With a lead and only 2 minutes left, running the ball burns clock and prevents turnovers. Each run play uses 5-8 seconds minimum, and first downs end the game.',
              },
              {
                id: 'fb-clock-sit2',
                type: 'situational' as const,
                question: 'What should the offense prioritize?',
                situationContext: '0:45 left in 2nd quarter | Opponent 40-yard line | Tied 14-14',
                options: [
                  'Kneel the ball and go to halftime',
                  'Run up the middle for safe yards',
                  'Quick passes to the sideline to stop the clock and get in field goal range',
                  'Run a trick play',
                ],
                correctIndex: 2,
                explanation: 'With 45 seconds at the opponent\'s 40, you\'re in scoring range. Quick sideline passes stop the clock while gaining yards for a field goal attempt before halftime.',
              },
              {
                id: 'fb-clock-match1',
                type: 'matching' as const,
                question: 'Match each action with its effect on the game clock.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Complete pass inbounds', 'Clock keeps running'],
                  ['Incomplete pass', 'Clock stops'],
                  ['Running out of bounds', 'Clock stops'],
                  ['Quarterback kneel', 'Clock runs, play ends'],
                ],
                explanation: 'Understanding which plays stop the clock is essential for managing end-of-half and end-of-game situations.',
              },
            ],
          },
          {
            id: 'fb-red-zone-strategy',
            title: 'Red Zone Offense',
            description: 'Scoring when the field shrinks.',
            conceptText: [
              'Inside the 20-yard line, the defense\'s job gets easier. The field compresses — there\'s less room for routes, and safeties can play closer to the line.',
              'Great red zone offenses use play-action, quick slants, and fade routes to the corners. The tight end becomes a premium weapon because they can exploit the middle of the field.',
              'Settling for field goals instead of touchdowns is the difference between good and great offenses. The best teams score TDs on 60%+ of red zone trips.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'fb-rz-q1',
                type: 'multiple-choice' as const,
                question: 'Why does the defense have an advantage in the red zone?',
                options: [
                  'The rules change inside the 20',
                  'The compressed field reduces space for the offense to operate',
                  'Defenders are allowed to hold receivers',
                  'The play clock is shorter',
                ],
                correctIndex: 1,
                explanation: 'The red zone compresses the field vertically, meaning safeties and linebackers can play closer to the action. Deep routes become impossible, limiting the offense\'s options.',
              },
              {
                id: 'fb-rz-sit1',
                type: 'situational' as const,
                question: 'What\'s the best play call?',
                situationContext: '3rd and Goal from the 4 | 3rd quarter | Down by 7',
                options: [
                  'Field goal to cut the lead to 4',
                  'Play-action pass to the tight end in the flat',
                  'Hail Mary to the back of the end zone',
                  'Quarterback draw up the middle',
                ],
                correctIndex: 1,
                explanation: 'Play-action freezes the linebackers, and a tight end in the flat at the 4-yard line is a high-percentage throw. You need a TD when down 7, so a field goal doesn\'t help much.',
              },
              {
                id: 'fb-rz-tf1',
                type: 'true-false' as const,
                question: 'The red zone starts at the opponent\'s 30-yard line.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! The red zone is from the opponent\'s 20-yard line to the goal line. Some analysts track a "green zone" from the 30, but the traditional red zone starts at the 20.',
              },
            ],
          },
        ],
      },
    ],
  },
  // ── New: Special Teams (Beginner) ────────────────────────
  {
    id: 'special-teams',
    difficulty: 'beginner' as DifficultyLevel,
    title: 'Special Teams',
    description: 'Kickoffs, punts, field goals, and the hidden third of football.',
    color: 'primary',
    units: [
      {
        id: 'kicking-game',
        title: 'The Kicking Game',
        icon: '🦵',
        requiredXp: 50,
        lessons: [
          {
            id: 'fb-kickoffs-punts',
            title: 'Kickoffs & Punts',
            description: 'The plays that change field position.',
            conceptText: [
              'Every half starts with a kickoff. The kicking team boots the ball to the receiving team, who tries to return it as far as possible. Touchbacks (the ball lands in the end zone) start at the 25-yard line.',
              'Punts happen on 4th down when the offense can\'t get a first down. The punter kicks the ball to flip field position. A great punt pins the opponent deep in their own territory.',
              'Special teams plays might seem boring, but they can swing games. A blocked punt, a kick return for a touchdown, or a muffed catch can completely change momentum!',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'fb-st-q1',
                type: 'multiple-choice' as const,
                question: 'Where does the receiving team start after a touchback on a kickoff?',
                options: ['The 20-yard line', 'The 25-yard line', 'The 30-yard line', 'Where the ball was caught'],
                correctIndex: 1,
                explanation: 'After a touchback on a kickoff, the receiving team starts at their own 25-yard line. This rule was changed from the 20 to encourage touchbacks and reduce high-speed collisions.',
              },
              {
                id: 'fb-st-match1',
                type: 'matching' as const,
                question: 'Match each special teams play with its purpose.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Kickoff', 'Start a half or follow a score'],
                  ['Punt', 'Give up possession to gain field position'],
                  ['Field Goal', 'Score 3 points from a set play'],
                  ['Extra Point', 'Score 1 point after a touchdown'],
                ],
                explanation: 'Special teams covers all kicking plays — each has a different strategic purpose in the flow of the game.',
              },
              {
                id: 'fb-st-tf1',
                type: 'true-false' as const,
                question: 'A team can only punt on 4th down.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! While punts almost always happen on 4th down, a team can legally punt on any down. It\'s just never strategically smart to do so earlier.',
              },
            ],
          },
          {
            id: 'fb-field-goals',
            title: 'Field Goals & PATs',
            description: 'The points that add up.',
            conceptText: [
              'A field goal is worth 3 points. The holder catches the snap and places it for the kicker. NFL kickers regularly make kicks from 40-50 yards out.',
              'After a touchdown, teams choose: kick an extra point (1 point, from the 15-yard line) or go for a two-point conversion (run a play from the 2-yard line). Extra points are nearly automatic but two-point tries succeed only about 50% of the time.',
              'Going for two is smart when the math favors it — like when it would tie the game or put you up by a full score. Coaches use "go-for-two" charts based on score differentials.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'fb-fg-sit1',
                type: 'situational' as const,
                question: 'Should the team kick the extra point or go for two?',
                situationContext: 'Just scored TD, now trailing 21-20 | 0:30 left in 4th | No timeouts',
                options: [
                  'Kick the extra point to tie it and go to overtime',
                  'Go for two to win the game outright',
                  'Take a knee to preserve the tie',
                  'Attempt an onside kick',
                ],
                correctIndex: 1,
                explanation: 'With 30 seconds left and no timeouts, overtime is risky. Going for two gives you a ~50% chance to win right now. The math favors the aggressive call.',
              },
              {
                id: 'fb-fg-q1',
                type: 'multiple-choice' as const,
                question: 'How many points is a field goal worth?',
                options: ['1 point', '2 points', '3 points', '6 points'],
                correctIndex: 2,
                explanation: 'A field goal is worth 3 points. It\'s the second most common scoring play after touchdowns (6 points).',
              },
            ],
          },
        ],
      },
    ],
  },
];

export function getTotalLessons(): number {
  return learningPaths.reduce((total, path) =>
    total + path.units.reduce((unitTotal, unit) =>
      unitTotal + unit.lessons.length, 0), 0);
}

export function findLesson(lessonId: string): { path: LearningPath; unit: Unit; lesson: Lesson } | null {
  for (const path of learningPaths) {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        if (lesson.id === lessonId) {
          return { path, unit, lesson };
        }
      }
    }
  }
  return null;
}

export function getNextLesson(currentLessonId: string, xp: number): Lesson | null {
  let found = false;
  for (const path of learningPaths) {
    for (const unit of path.units) {
      if (xp < unit.requiredXp) continue;
      for (const lesson of unit.lessons) {
        if (found) return lesson;
        if (lesson.id === currentLessonId) found = true;
      }
    }
  }
  return null;
}
